import { format, addDays, isSameDay, addMonths, startOfMonth, endOfMonth, startOfQuarter, endOfQuarter, startOfYear, endOfYear, add, subMonths, isAfter } from "date-fns";
import { currencies, countries } from "../Components/Enums/DataEnums";
import store from '../Store/Store';
import { Headphones, BriefcaseBusiness, BookCopy, CalendarDays, Globe2, Layers, Smile, FileText, Laugh, Angry, Zap, Star, Crown, Flame } from 'lucide-react';
import { BsCartFill } from "react-icons/bs";
import SahiIcon from '../Components/Common/SahiIcon'; // Adjust path as needed
import { faFilterCircleDollar } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getTransactionTypesArr } from '../Store/Reducers';
import { getAuthData } from "./AuthUtils";
import { getStorageData, getActiveData, STORAGE_KEYS } from "./Storage";
import { API_CONFIG } from "./APIConfig";
import { Frown, Meh } from "lucide-react";
import { toast } from "sonner";
import { addMinutes } from "date-fns";
import { getPriorityDetails } from "./Constants";
import { TESTING_AGENCIES } from "./FeatureFlags";

// MonthlyThreads bucket sub-fields (Usrs/Prds) come back either as a raw JSON string
// (legacy rows, not yet rewritten by core_agn_SetMonthlyStatsBulk after its JSON_QUERY fix)
// or already as a parsed array (rows written after the fix, where the parent JSON.parse
// on MonthlyThreads already resolved the nested array). Handles both safely.
export const safeParseJsonArray = (val) => {
  if (Array.isArray(val)) return val;
  if (typeof val === 'string') {
    try { return JSON.parse(val || '[]') || []; } catch { return []; }
  }
  return [];
};

export const getAt99 = false;
export const SCRM_LOGIN_MODE = "MultiAgency"; // Options: "SeparateAgency", "MultiAgency"

// Function for checking URL is from live or not
export const isLive = () => {
  let hostName = (window.location.hostname).toLowerCase();
  if (hostName.includes("sahicrm.com") || hostName.includes("sahi.club") || hostName.includes("sahiclub.com")) {
    return true;
  }
  return false;
};

export const dateDiff = (fromDT, toDT, param = "DD") => {
  if (!(fromDT instanceof Date) || !(toDT instanceof Date)) {
    throw new TypeError("Both arguments must be Date instances.");
  }

  const from = new Date(fromDT);
  const to = new Date(toDT);

  if (param === "DD") {
    from.setHours(0, 0, 0, 0);
    to.setHours(0, 0, 0, 0);
    return Math.floor((to - from) / (1000 * 60 * 60 * 24));
  }
  return null;
}

export const isValidTypeID = (typeID, threadTypes = []) => {
  const id = parseInt(typeID, 10);
  if (!Number.isInteger(id)) return "Thread";

  const found = threadTypes.find(t => t.TID === id);
  return found?.Typ || "Thread";
};

export const threadTypeMeta = {
  1: {
    icon: faFilterCircleDollar,
    isFontAwesome: true,
    colorClass: "text-sahi-orange",
  },
  2: {
    icon: BsCartFill,
    colorClass: "text-sahi-orange",
    isFontAwesome: false,
  },
  3: {
    icon: Headphones, // Lucide icon
    colorClass: "text-sahi-green",
  },
  4: {
    icon: faFilterCircleDollar,
    isFontAwesome: true,
    colorClass: "text-sahi-green",
  },
  5: {
    icon: BsCartFill, // Lucide icon
    colorClass: "text-sahi-red",
  },
  6: {
    icon: Headphones, // Lucide icon
    colorClass: "bg-sahi-dark-blue/10 text-sahi-dark-blue",
  },
  7: {
    icon: BriefcaseBusiness, // Lucide icon
    colorClass: "bg-sahi-brown/10 text-sahi-brown",
  },
  8: {
    icon: BookCopy, // Lucide icon
    colorClass: "bg-sahi-green-light text-sahi-green-700",
  },
  9: {
    icon: CalendarDays, // Lucide icon
    colorClass: "bg-yellow-100 text-yellow-700",
  },
  10: {
    icon: Globe2, // Lucide icon
    colorClass: "bg-sahi-grey/20 text-sahi-grey",
  },
};

export const getTypeIdFromThread = (threadName, threadTypes = []) => {
  if (typeof threadName !== "string") return 1;
  const match = threadTypes.find(t =>
    t.Typ.toLowerCase().includes(threadName.toLowerCase())
  );
  return match?.TID || 1;
};

export const getThreadNameFromTypeID = (typeID, threadTypes = [], key = "TypSng") => {
  const found = threadTypes.find(t => t.TID === parseInt(typeID));
  let name = found?.[key] || "Thread";
  if (name === "Order") return "Sale";
  if (name === "Orders") return "Sales";
  if (name === "order") return "sale";
  if (name === "orders") return "sales";
  return name;
};

export const pluralizeIfThread = (name) => {
  if (typeof name !== 'string') return name;

  return name.toLowerCase() === 'thread' ? 'threads' : name;
};

export const getThreadTypeMeta = (typeID, threadTypes = []) => {
  const thread = threadTypes.find(t => t.TID === parseInt(typeID));
  const parsedTypeID = parseInt(typeID);

  let colorClass = "";
  let textColor = "";

  if ([1, 2, 3].includes(parsedTypeID)) {
    colorClass = "text-sahi-green";
    textColor = "var(--sahi-green)";
  }
  else if ([4, 5, 6].includes(parsedTypeID)) {
    colorClass = "text-sahi-orange";
    textColor = "var(--sahi-orange)";
  }
  else {
    colorClass = "text-sahi-red";
    textColor = "var(--sahi-red)";
  }

  const meta = threadTypeMeta[parsedTypeID] || {};
  // Create a render function that handles both icon types
  const IconComponent = ({ className = "" }) => {
    if (meta.isFontAwesome) {
      return <FontAwesomeIcon icon={meta.icon} className={className} />;
    } else {
      const LucideIcon = meta.icon || Layers;
      return <LucideIcon className={className} />;
    }
  };

  if (!thread) {
    return {
      name: "Thread",
      nameSng: "Thread",
      Icon: IconComponent,
      colorClass: colorClass,
      textColor: textColor,
    };
  }

  const formatTypeName = (name) => {
    if (name === "Order") return "Sale";
    if (name === "Orders") return "Sales";
    if (name === "order") return "sale";
    if (name === "orders") return "sales";
    return name;
  };

  return {
    name: formatTypeName(thread.Typ),
    nameSng: formatTypeName(thread.TypSng),
    nameSnSng: formatTypeName(thread.SnSng),
    Icon: IconComponent,
    colorClass: colorClass,
    textColor: textColor,
  };
};

export const getThreadMenuItems = (threadTypes = []) =>
  threadTypes.map(({ TID, Typ }) => {
    const meta = threadTypeMeta[TID] || {
      icon: Layers,
      colorClass: "bg-gray-100 text-gray-600",
      isFontAwesome: false,
    };
    return {
      text: Typ,
      icon: meta.icon,
      colorClass: meta.colorClass,
      path: `/threads?tid=${TID}`,
      count: 0,
      type: TID,
      isFontAwesome: meta.isFontAwesome || false,
    };
  });

export const isSalesOnlyActive = (threadTypesArr = []) => {
  if (!Array.isArray(threadTypesArr)) return false;
  //return [1, 2, 3].every(tid => threadTypesArr.some(t => Number(t?.TID) === tid));

  const activeNonSalesTypes = threadTypesArr.filter(item => item.Sh && item.Flow == 'Out');
  return (activeNonSalesTypes.length === 0);
};

export const combineDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const [hours24, minutes] = timeStr.split(":").map(Number);
  let hours12 = hours24 % 12;
  hours12 = hours12 ? hours12 : 12;
  const ampm = hours24 >= 12 ? "PM" : "AM";
  return `${dateStr} ${hours12.toString().padStart(2, "0")}:${minutes
    .toString()
    .padStart(2, "0")} ${ampm}`;
};

export const normalizeDateFormat = (dateStr) => {
  if (!dateStr) return dateStr;

  if (typeof dateStr === 'number' || /^\d+$/.test(String(dateStr).trim())) {
    const serialNumber = parseInt(dateStr, 10);
    if (serialNumber > 0 && serialNumber < 100000) {
      try {
        const excelEpoch = new Date(1900, 0, 1);
        let adjustment = 1;
        if (serialNumber > 60) {
          adjustment = 2;
        }
        const date = new Date(excelEpoch.getTime() + (serialNumber - adjustment) * 24 * 60 * 60 * 1000);

        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        return dateStr;
      }
    }
  }

  const trimmed = String(dateStr).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    return trimmed;
  }

  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
    const parts = trimmed.split('/');
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }

  if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) {
    const parts = trimmed.split('-');
    const month = parts[0].padStart(2, '0');
    const day = parts[1].padStart(2, '0');
    const year = parts[2];
    return `${year}-${month}-${day}`;
  }

  return dateStr;
};

export const getThreadName = (actionType) => {
  const name = actionType || "thread";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

export function pluralizeWord(word) {
  if (typeof word !== "string") return word;
  if (word === "all") return "All";

  if (/[b-df-hj-np-tv-z]y$/i.test(word)) {
    return word.replace(/y$/i, "ies");
  }

  if (/(s|ss|sh|ch|x|z)$/i.test(word)) {
    return word + "es";
  }

  const irregulars = {
    mouse: "mice",
    person: "people",
    goose: "geese",
    child: "children",
  };
  const lower = word.toLowerCase();
  if (irregulars[lower]) {
    const plural = irregulars[lower];
    return word[0] === word[0].toUpperCase()
      ? plural.charAt(0).toUpperCase() + plural.slice(1)
      : plural;
  }

  return word + "s";
}

// Add this helper function near the top of your component
export const getDateWithoutTime = (dateValue) => {
  const dateText = getRelativeDateText(dateValue);
  // Remove time portion (everything after and including the parentheses)
  return dateText.replace(/\s*\([^)]*\)/, '');
};

// Returns relative date text like "Today", "Yesterday", "Tomorrow", or formatted date
export const getRelativeDateText = (
  dateString,
  applyColor = false,
  showTime = true,
  showTimeAlways = false,
  forceDateOnly = false, // 👈 new param
  forceTimeOnly = false // 👈 new param
) => {
  if (!dateString) return "--";

  const taskDate = new Date(dateString);
  const today = new Date();
  const tomorrow = new Date();
  const yesterday = new Date();

  // Create date copies for comparison (without time)
  const todayCompare = new Date(today);
  const tomorrowCompare = new Date(tomorrow);
  const yesterdayCompare = new Date(yesterday);
  const taskDateCompare = new Date(taskDate);

  todayCompare.setHours(0, 0, 0, 0);
  tomorrowCompare.setDate(today.getDate() + 1);
  tomorrowCompare.setHours(0, 0, 0, 0);
  yesterdayCompare.setDate(today.getDate() - 1);
  yesterdayCompare.setHours(0, 0, 0, 0);
  taskDateCompare.setHours(0, 0, 0, 0);

  // Format time in 12-hour format
  const formatTime = (date) => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? "PM" : "AM";
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, "0");
    return `${displayHours}:${displayMinutes} ${ampm}`;
  };

  // Format date as "5-Sep"
  //   const formatDateShort = (dateString) => {
  //     if (!dateString) return "--";
  //     const date = new Date(dateString);
  //     const day = date.getDate();
  //     const monthNames = [
  //       "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  //       "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  //     ];
  //     const month = monthNames[date.getMonth()];
  //     return `${day}-${month}`;
  //   };


  const formatDateShort = (dateString) => {
    if (!dateString) return "--";
    const date = new Date(dateString);
    const currentYear = new Date().getFullYear();
    const targetYear = date.getFullYear();
    const day = date.getDate();
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const month = monthNames[date.getMonth()];
    // Only append year if it's not the current year
    if (targetYear !== currentYear) {
      const yearShort = targetYear.toString().slice(-2);
      return `${day}-${month}-${yearShort}`;
    }

    return `${day}-${month}`;
  };

  // Get the appropriate color for the date
  const getDateColor = () => {
    if (taskDateCompare.getTime() === todayCompare.getTime()) {
      return "text-sahi-green";
    } else if (taskDateCompare.getTime() === tomorrowCompare.getTime()) {
      return "text-sahi-blue";
    } else if (taskDateCompare.getTime() === yesterdayCompare.getTime()) {
      return "text-sahi-red";
    } else if (taskDateCompare < todayCompare) {
      return "text-sahi-red";
    } else {
      return "text-sahi-blue";
    }
  };

  let dateText = "";

  if (forceDateOnly) {
    // 👈 always fallback to formatted date
    dateText = !showTimeAlways
      ? formatDateShort(dateString)
      : `${formatDateShort(dateString)} (${formatTime(taskDate)})`;
  } else {
    if (taskDateCompare.getTime() === todayCompare.getTime()) {
      dateText = showTime ? (forceTimeOnly ? `${formatTime(taskDate)}` : `Today (${formatTime(taskDate)})`) : "Today";
    } else if (taskDateCompare.getTime() === tomorrowCompare.getTime()) {
      dateText = showTime ? `Tmrw (${formatTime(taskDate)})` : "Tmrw";
    } else if (taskDateCompare.getTime() === yesterdayCompare.getTime()) {
      dateText = !showTimeAlways
        ? "Yday"
        : `Yday (${formatTime(taskDate)})`;
    } else {
      dateText = !showTimeAlways
        ? formatDateShort(dateString)
        : `${formatDateShort(dateString)} (${formatTime(taskDate)})`;
    }
  }

  // Apply color if requested
  if (applyColor) {
    const colorClass = getDateColor();
    return <span className={colorClass}>{dateText}</span>;
  }

  return dateText;
};

// Format date as "25-Dec-26"
export const formatDateFull = (dateString) => {
  if (!dateString) return "--";
  const date = new Date(dateString);
  const currentYear = new Date().getFullYear();
  const targetYear = date.getFullYear();

  const day = date.getDate().toString().padStart(2, "0");
  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];
  const month = monthNames[date.getMonth()];

  // Only append year if it's not the current year
  if (targetYear !== currentYear) {
    const yearShort = targetYear.toString().slice(-2);
    return `${day}-${month}-${yearShort}`;
  }

  return `${day}-${month}`;
};
// Returns color class for task dates based on timing
export function getLastDateColor(dateStr) {
  if (!dateStr) return "text-sahi-red";

  const givenDate = new Date(dateStr);
  const today = new Date();

  if (givenDate > today) {
    givenDate.setFullYear(today.getFullYear() - 1);
  }

  today.setHours(0, 0, 0, 0);
  givenDate.setHours(0, 0, 0, 0);

  const differenceInDays = Math.round(
    (today - givenDate) / (1000 * 60 * 60 * 24)
  );
  const differenceInMonths = differenceInDays / 30;

  if (differenceInDays <= 1) return "text-sahi-green";
  if (differenceInMonths <= 3) return "text-sahi-blue";
  return "text-sahi-red";
}

export const getDueColor = (DT) => {
  if (!DT) return "var(--sahi-red)";

  const TaskDate = new Date(DT);
  if (isNaN(TaskDate)) return "var(--sahi-red)";

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  TaskDate.setHours(0, 0, 0, 0);

  if (TaskDate.getTime() === today.getTime()) {
    return "var(--sahi-green)";
  } else if (TaskDate.getTime() > today.getTime()) {
    return "var(--sahi-blue)";
  } else {
    return "var(--sahi-red)";
  }
};


export const getTaskColor = (TaskDT) => {
  if (!TaskDT) return "var(--sahi-red)";

  const TaskDate = new Date(TaskDT);
  if (isNaN(TaskDate)) return "var(--sahi-red)";

  const today = new Date();
  const tomorrow = new Date();

  today.setHours(0, 0, 0, 0);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  TaskDate.setHours(0, 0, 0, 0);

  // if (TaskDate.getTime() === today.getTime()) {
  //   return "var(--sahi-green)";
  // } else if (TaskDate.getTime() === tomorrow.getTime()) {
  //   return "var(--sahi-blue)";
  // } else if (TaskDate < today) {
  //   return "var(--sahi-red)";
  // } else {
  //   return "var(--sahi-blue)";
  // }

  if (TaskDate.getTime() >= today.getTime()) {
    return "var(--sahi-green)";
  } else {
    return "var(--sahi-red)";
  }
};

// Maps task/thread colors to CSS classes
export const getColorClass = (colorVar) => {
  if (colorVar === "var(--sahi-green)" || colorVar === "text-sahi-green") return "text-sahi-green";
  if (colorVar === "var(--sahi-blue)" || colorVar === "text-sahi-blue") return "text-sahi-blue";
  if (colorVar === "var(--sahi-red)" || colorVar === "text-sahi-red") return "text-sahi-red";
  return "text-gray-700";
};

export const formatDate = (dateString) => {
  if (!dateString) return "-";
  try {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    const tomorrow = new Date(today);

    yesterday.setDate(today.getDate() - 1);
    tomorrow.setDate(today.getDate() + 1);

    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    tomorrow.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);

    if (date.getTime() === today.getTime()) {
      return "Today";
    } else if (date.getTime() === yesterday.getTime()) {
      return "Yday";
    } else if (date.getTime() === tomorrow.getTime()) {
      return "Tmrw";
    } else {
      const formatted = date
        .toLocaleDateString("en-GB", {
          day: "numeric",
          month: "short",
        })
        .replace(" ", "-");
      return formatted.replace(/Sept/i, "Sep");
    }
  } catch {
    return dateString;
  }
};

export const formatDateShort = (dateString) => {
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  let month = date.toLocaleString("en-US", { month: "short" });
  if (month.toLowerCase() === "sept") month = "Sep";
  return `${day}-${month}`;
};

export const formatTime = (dateString) => {
  if (!dateString) return "--";
  try {
    const date = new Date(dateString);
    return date
      .toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .toUpperCase();
  } catch {
    return "--";
  }
};

// Returns badge styling for thread types
export const getThreadTypeInfo = (type) => {
  const types = {
    I: {
      name: "Inquiry",
      color: "bg-sahi-orange-light text-sahi-orange",
      icon: "MessageCircle",
    },
    O: {
      name: "Order",
      color: "bg-sahi-green-light text-sahi-green",
      icon: "MessageCircle",
    },
    S: {
      name: "Support",
      color: "bg-sahi-red-light text-sahi-red",
      icon: "MessageCircle",
    },
  };
  return (
    types[type.toUpperCase()] || {
      name: "Unknown",
      color: "bg-gray-400 text-white",
      icon: "MessageCircle",
    }
  );
};


// Returns badge styling for record types
export const getRecordTypeInfo = (type) => {
  const types = {
    INXN: {
      name: "Interaction",
      color: "bg-sahi-orange-light text-sahi-orange",
    },
    MEETING: { name: "Meeting", color: "bg-sahi-green-light text-sahi-green" },
    CALL: { name: "Call", color: "bg-sahi-blue-light text-sahi-blue" },
    EMAIL: { name: "Email", color: "bg-sahi-red-light text-sahi-red" },
  };
  return (
    types[type] || {
      name: type || "Record",
      color: "bg-gray-100 text-gray-700",
    }
  );
};

// Copies text to clipboard and shows feedback
export const copyToClipboard = async (text, type, setCopiedText) => {
  try {
    await navigator.clipboard.writeText(text);
    setCopiedText(type);
    setTimeout(() => setCopiedText(""), 2000);
  } catch (err) {
    console.error("Failed to copy text: ", err);
  }
};

// Calculates tomorrow's date for default next action
export const getTomorrowDate = () => {
  const tomorrow = addDays(new Date(), 1);
  return format(tomorrow, "dd-MMM-yy");
};
export const getTodayDate = () => {
  const today = new Date();
  return format(today, 'dd-MMM-yy');
};

// Returns default time for next action
export const getDefaultTime = () => "11:00";

// Returns formatted location string for display
export const getLocationString = (city, state, country) => {
  return (
    [city, state, country].filter(Boolean).join(", ") ||
    "Location not specified"
  );
};

export const createPhoneLink = (mobile, countryCode = '91') => {
  const cleanMobile = mobile.replace(/\D/g, '');
  return `tel:+${countryCode}${cleanMobile}`;
};

// Creates WhatsApp link for messaging with country code
export const createWhatsAppLink = (mobile, countryCode = '91') => {
  const cleanMobile = mobile.replace(/\D/g, '');
  return `https://wa.me/${countryCode}${cleanMobile}`;
};

// Creates email link for mailing
export const createEmailLink = (email) => `mailto:${email}`;

// Creates Google Maps link for location
export const createMapsLink = (location) => {
  const [lat, lng] = location.split(",");
  return `https://maps.google.com?q=${lat},${lng}`;
};

// Returns proper title case name
export const getTitleCase = (text) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};
export const timeAgo = (d) => {
  const diff = Math.floor((Date.now() - new Date(d)) / (1000 * 60 * 60 * 24));
  return diff < 1
    ? "Today"
    : diff === 1
      ? "1 day ago"
      : diff < 30
        ? `${diff} days ago`
        : diff < 365
          ? `${Math.floor(diff / 30)} month${Math.floor(diff / 30) > 1 ? "s" : ""
          } ago`
          : `${Math.floor(diff / 365)} year${Math.floor(diff / 365) > 1 ? "s" : ""
          } ago`;
};

export const capitalizeFirstLetter = (str = "") =>
  str ? str[0].toUpperCase() + str.slice(1).toLowerCase() : "";


export const getThreadTypeColor = (typeName) => {
  if (!typeName) return 'bg-gray-400 text-white';

  const lowerType = typeName.toLowerCase();

  if (lowerType.includes('inquir')) {
    return 'bg-sahi-orange-light text-sahi-orange border-sahi-orange';
  } else if (lowerType.includes('order')) {
    return 'bg-sahi-green-light text-sahi-green border-sahi-green';
  } else if (lowerType.includes('support')) {
    return 'bg-sahi-red-light text-sahi-red border-sahi-red';
  }

  return 'bg-blue-100 text-blue-700 border-blue-200';
};


// Check selected time is not in future
// selectedDate format : 23-Jul-25
// selectedTime format : 10:57 AM
export const isSelectedDateTimeInFuture = (selectedDate, selectedTime) => {
  if (selectedDate == null || selectedTime == null) {
    return false;
  }
  const now = new Date();
  const todayFormatted = format(now, "dd-MMM-yy");

  // If selected date is not today, compare the date directly
  if (selectedDate !== todayFormatted) {
    const parsedSelectedDate = new Date(selectedDate); // Should be in a parseable format
    return parsedSelectedDate > now;
  }

  // If it's today, compare time
  const [hourStr, minuteStr] = selectedTime.split(":");
  const hour = parseInt(hourStr, 10);
  const minute = parseInt(minuteStr, 10);
  const selectedDateTime = new Date();
  selectedDateTime.setHours(hour, minute, 0, 0); // set selected time on today's date
  return selectedDateTime > now;
};


export const getPriorityInfo = (priority) => {
  return getPriorityDetails(priority).class;
};

// Update the getStatusInfo function in your utilities (or add it if missing):
export const getStatusInfo = (status) => {
  // Handle both numeric and string status
  let statusText = '';
  let statusClass = '';

  if (status === 1 || status === '1' || status === 'Pending') {
    statusText = 'Pending';
    statusClass = 'text-sahi-orange border-sahi-orange';
  } else if (status === 2 || status === '2' || status === 'Completed') {
    statusText = 'Completed';
    statusClass = 'text-sahi-green border-sahi-green';
  } else if (status === 0 || status === '0' || status === 'Cancelled') {
    statusText = 'Cancelled';
    statusClass = 'text-sahi-red border-sahi-red';
  } else {
    statusText = 'Pending';
    statusClass = 'text-sahi-orange border-sahi-orange';
  }

  return { text: statusText, class: statusClass };
};

export const renderDateBadge = (dateString, isTask = false) => {
  const color = isTask ? getTaskColor(dateString) : getLastDateColor(dateString);

  // Map colors to consistent background and text classes
  let bgClass = '';
  let textClass = '';

  if (color === "text-sahi-green") {
    bgClass = "bg-sahi-green-light";
    textClass = "text-sahi-green";
  } else if (color === "text-sahi-blue") {
    bgClass = "bg-sahi-blue-light";
    textClass = "text-sahi-blue";
  } else if (color === "text-sahi-red") {
    bgClass = "bg-sahi-red-light";
    textClass = "text-sahi-red";
  } else {
    // Default fallback
    bgClass = "bg-sahi-red-light";
    textClass = "text-sahi-red";
  }

  return (
    <div className={`sahi-badge-base ${bgClass} ${textClass}`}>
      {getRelativeDateText(dateString)}
    </div>
  );
};

// format inr amount for mobile view 
export const toAllWordsLowerCase = (str) => {
  return str?.split(" ").map(w => w.toLowerCase()).join(" ") || "";
};


export function formatAmount(amt, countryID) {
  if (isNaN(amt) || amt < 0) return "0";
  const country = countries.find(c => c.CountryID === countryID);

  // Helper: always round to integer, add commas
  const formatInt = (value) => {
    return Math.round(value).toLocaleString("en-US");
  };

  // Indian Format
  if (country && country.CountryID === 1) {

    let value;
    let suffix;

    if (amt < 10000) {              // up to 9999
      value = amt;
      suffix = "";
    } else if (amt < 1000000) {      // 10K – 999K
      value = amt / 1000;
      suffix = "K";
    } else if (amt < 100000000) {    // 10L – 999L
      value = amt / 100000;
      suffix = "L";
    } else {                        // 1Cr and above
      value = amt / 10000000;
      suffix = "Cr";
    }

    if (!isMobileDevice()) {
      return formatInt(amt);
    }

    return formatInt(value) + (suffix ? " " + suffix : "");
  }

  // International Format
  else {
    let value;
    let suffix;

    if (amt < 10000) {        // up to 9999
      value = amt;
      suffix = "";
    } else if (amt < 1000000) {  // up to 999K
      value = amt / 1000;
      suffix = "K";
    } else if (amt < 1000000000) {  // up to 999M
      value = amt / 1000000;
      suffix = "M";
    } else {                        // 1B+
      value = amt / 1000000000;
      suffix = "B";
    }

    return formatInt(value) + (suffix ? " " + suffix : "");
  }
}


export const getConnectionTypeLabel = (connType) => {
  if (!connType) return null;

  const upperType = connType.toUpperCase();
  const types = [];

  for (const char of upperType) {
    switch (char) {
      case 'C':
        types.push('Customer');
        break;
      case 'S':
        types.push('Supplier');
        break;
      default:
        break;
    }
  }

  // If both Customer and Supplier, return "Connection Details"
  if (types.length === 2) {
    return 'Contact Details';
  }

  return types.join(', ');
};

export const getTodayTimeDisplayText = (dateString, showTime = true) => {
  if (!dateString) return "--";

  const inputDate = new Date(dateString);
  const today = new Date();

  // Create date copies for comparison (without time)
  const todayCompare = new Date(today);
  const taskDateCompare = new Date(inputDate);

  todayCompare.setHours(0, 0, 0, 0);
  taskDateCompare.setHours(0, 0, 0, 0);

  // If it's today, show only time
  if (taskDateCompare.getTime() === todayCompare.getTime() && showTime) {
    const hours = inputDate.getHours();
    const minutes = inputDate.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  }

  return getRelativeDateText(dateString, false, showTime);

};


export const capitaliseString = (recordType) => {
  return recordType[0].toUpperCase() + recordType.substring(1, recordType.length);
}

// Utils/TaskTimeValidation.js or add to existing utilities file
export const validateTaskDateTime = (date, time, isNextTask = false) => {
  if (!date || !time) return { isValid: true };

  const taskDate = new Date(date);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  taskDate.setHours(0, 0, 0, 0);

  // If task date is today, validate that time is in the future
  if (taskDate.getTime() === today.getTime()) {
    const now = new Date();
    const [hours, minutes] = time.split(':');
    const taskDateTime = new Date();
    taskDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    if (taskDateTime <= now) {
      return {
        isValid: false,
        message: "Invalid Time Selection",
        description: isNextTask
          ? "Please select a future time. Past time is not allowed for today's tasks."
          : "Please select a future time. Past time is not allowed for today's records."
      };
    }
  }

  return { isValid: true };
};

export const getNextHourTime = () => {
  const futureTime = new Date();
  futureTime.setHours(futureTime.getHours() + 1);
  return futureTime.toTimeString().slice(0, 5);
};


export const getSentimentIcon = (sentiment) => {
  // Default to happy/smile icon if sentiment is empty or undefined
  if (!sentiment || sentiment.trim() === '') {
    return <Smile className="h-7 w-7 text-sahi-green" />; // Increased from h-4 w-4
  }

  const sentimentLower = sentiment.toLowerCase();

  if (sentimentLower.includes('happy') || sentimentLower.includes('positive') || sentimentLower.includes('good')) {
    return <Smile className="h-7 w-7 text-sahi-green" />;
  } else if (sentimentLower.includes('sad') || sentimentLower.includes('negative') || sentimentLower.includes('bad')) {
    return <Smile className="h-7 w-7 text-sahi-red" />;
  } else if (sentimentLower.includes('neutral')) {
    return <Smile className="h-7 w-7 text-gray-500" />;
  } else if (sentimentLower.includes('angry')) {
    return <Smile className="h-7 w-7 text-sahi-red" />;
  } else {
    return <Smile className="h-7 w-7 text-sahi-green" />;
  }
};

export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

// Check if device is iOS
export const isIOSDevice = () => {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent);
};

// Check if device is Android
export const isAndroidDevice = () => {
  return /Android/i.test(navigator.userAgent);
};

// Open URL in external browser (for WebViewGold apps and Web Safari/iOS navigation)
export const openInExternalBrowser = (url, preopenedWindow = null) => {
  if (!url) {
    if (preopenedWindow && !preopenedWindow.closed) preopenedWindow.close();
    return;
  }
  if (window.flutter_inappwebview) {
    if (preopenedWindow && !preopenedWindow.closed) preopenedWindow.close();
    window.flutter_inappwebview.callHandler('openExternalBrowser', url);
  } else if (window.Android && window.Android.openExternalBrowser) {
    if (preopenedWindow && !preopenedWindow.closed) preopenedWindow.close();
    window.Android.openExternalBrowser(url);
  } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openExternalBrowser) {
    if (preopenedWindow && !preopenedWindow.closed) preopenedWindow.close();
    window.webkit.messageHandlers.openExternalBrowser.postMessage(url);
  } else if (preopenedWindow && !preopenedWindow.closed) {
    preopenedWindow.location.href = url;
  } else {
    window.open(url, '_blank');
  }
};

// Check if Contacts API is supported (mainly Android)
export const isContactPickerAPISupported = () => {
  return 'contacts' in navigator && 'ContactsManager' in window;
};

// Check if Contact Picker is available on iOS via webkit
export const isIOSContactPickerSupported = () => {
  return isIOSDevice() && window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.pickContact;
};

// Check if device has WebViewGold contact capabilities (both Android and iOS)
export const isWebViewGoldContactsSupported = () => {
  // If we've detected it's a Mobile App (not a browser), 
  // we assume it's our wrapper and support the schemes.
  return isMobileApp();
};

// Check Contact Picker API support (includes iOS and Android)
export const isContactPickerSupported = () => {
  // For iOS: check webkit contact picker or WebViewGold
  if (isIOSDevice()) {
    return isIOSContactPickerSupported() || isWebViewGoldContactsSupported();
  }
  // For Android: check Contacts API or WebViewGold
  if (isAndroidDevice()) {
    return isContactPickerAPISupported() || isWebViewGoldContactsSupported();
  }
  // Fallback check for any device
  return isContactPickerAPISupported() || isWebViewGoldContactsSupported();
};

export const getDateRangeOptions = () => [
  "This Month",
  "Other Month",
  "This Quarter",
  "Other Quarter",
  "This Year",
  "Other Year",
];

export const getRoundedUpTime = () => {
  const currentTime = new Date();
  // Round up to next hour: if minutes > 0, add 1 to hour
  if (currentTime.getMinutes() > 0) {
    currentTime.setHours(currentTime.getHours() + 1);
  }
  // Set minutes and seconds to 0 for exact hour
  currentTime.setMinutes(0, 0, 0);
  return currentTime.toTimeString().split(':').slice(0, 2).join(':');
};

export const getLast36MonthsOptions = () => {
  const options = [];
  const now = new Date();

  for (let i = 0; i < 36; i++) {
    const date = subMonths(now, i);
    const label = format(date, "MMM yyyy"); // e.g. "Jan 2024"

    options.push({ label: label, value: label });
  }

  return options;
};

export const months = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

export const getOtherMonthOptions = () => {
  const currentYear = new Date().getFullYear();
  const currentMonthIndex = new Date().getMonth(); // 0 = Jan
  const options = [];

  // Current year months (skip current month)
  months.forEach((month, idx) => {
    if (idx !== currentMonthIndex) {
      const value = `${month} ${currentYear}`;
      options.push({ name: value, ID: value });
    }
  });

  // Next year months (all)
  months.forEach((month) => {
    const value = `${month} ${currentYear + 1}`;
    options.push({ name: value, ID: value });
  });

  return options;
};

export const getMonthsFromMonthlyXns = () => {
  const activeData = getActiveData();
  const monthlyXns = JSON.parse(activeData.MonthlyXns)

  if (!monthlyXns || monthlyXns.length === 0) return [];

  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1; // 1–12

  const validData = monthlyXns.filter(item => item.Cnt > 0);
  if (validData.length === 0) return [];

  // Find oldest year+month where Cnt > 0
  let oldest = validData.reduce((min, item) => {
    if (item.YY < min.YY || (item.YY === min.YY && item.MM < min.MM)) {
      return item;
    }
    return min;
  }, validData[0]);

  const results = [];
  let year = oldest.YY;
  let month = oldest.MM;

  // Loop from oldest until current year+month
  while (year < currentYear || (year === currentYear && month <= currentMonth)) {
    const label = `${months[month - 1]} ${year}`;
    results.push({ name: label, ID: label });

    month++;
    if (month > 12) {
      month = 1;
      year++;
    }
  }

  return results;
};

export const getQuartersFromTransactions = (txnData = []) => {
  if (!txnData || txnData.length === 0) return [];

  const quarters = new Set();

  txnData.forEach(transaction => {
    if (transaction.xnDT) {
      const date = new Date(transaction.xnDT);
      const year = date.getFullYear();
      const month = date.getMonth() + 1; // getMonth() returns 0-11
      const quarter = Math.ceil(month / 3);

      quarters.add(`Q${quarter} ${year}`);
    }
  });

  return Array.from(quarters)
    .sort((a, b) => {
      // Sort by year first, then by quarter
      const [quarterA, yearA] = a.split(' ');
      const [quarterB, yearB] = b.split(' ');

      if (yearA !== yearB) {
        return parseInt(yearB) - parseInt(yearA); // Newest year first
      }

      return parseInt(quarterB.replace('Q', '')) - parseInt(quarterA.replace('Q', '')); // Newest quarter first
    })
    .map(quarter => ({ name: quarter, ID: quarter }));
};

export const getYearsFromTransactions = (txnData = []) => {
  if (!txnData || txnData.length === 0) return [];

  const years = new Set();

  txnData.forEach(transaction => {
    if (transaction.xnDT) {
      const date = new Date(transaction.xnDT);
      const year = date.getFullYear();
      years.add(year);
    }
  });

  return Array.from(years)
    .sort((a, b) => b - a) // Newest year first
    .map(year => ({ name: year.toString(), ID: year.toString() }));
};

export const getPastMonthOptions = (targetDate) => {
  const options = [];
  const currentDate = new Date();
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  // Normalize targetDate (if passed)
  const parsedTargetDate = targetDate ? new Date(targetDate) : null;

  // Case 1: No target date OR future date → last 12 past months
  if (!parsedTargetDate || parsedTargetDate >= currentDate) {
    for (let i = 1; i <= 12; i++) {
      const pastDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      const monthName = months[pastDate.getMonth()];
      const year = pastDate.getFullYear();
      const value = `${monthName} ${year}`;
      options.push({ name: value, ID: value });
    }
  } else {
    // Case 2: Target date in past → include current month and go back till targetDate
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const targetYear = parsedTargetDate.getFullYear();
    const targetMonth = parsedTargetDate.getMonth();

    // Total months between target and current (inclusive)
    const totalMonths = (currentYear - targetYear) * 12 + (currentMonth - targetMonth) + 1;

    for (let i = 0; i < totalMonths; i++) {
      let adjustedMonth = currentMonth - i;
      let adjustedYear = currentYear;

      // Handle rollover across years
      if (adjustedMonth < 0) {
        adjustedYear -= Math.ceil(Math.abs(adjustedMonth) / 12);
        adjustedMonth = (12 + (adjustedMonth % 12)) % 12;
      }

      const monthName = months[adjustedMonth];
      const year = adjustedYear;
      const value = `${monthName} ${year}`;
      options.push({ name: value, ID: value });
    }
  }

  return options;
};


export const getOtherQuarterOptions = () => {
  const quarters = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;

  for (let year = currentYear; year >= currentYear - 5; year--) {
    for (let quarter = 4; quarter >= 1; quarter--) {
      // Skip current quarter of current year
      if (year === currentYear && quarter === currentQuarter) continue;
      const q = `Q${quarter} ${year}`;
      quarters.push({ name: q, ID: q });
    }
  }

  return quarters;
};

export const getPastQuarterOptions = (targetDate) => {
  const quarters = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarter = Math.floor(now.getMonth() / 3) + 1;

  const parsedTargetDate = targetDate ? new Date(targetDate) : null;

  if (!parsedTargetDate || parsedTargetDate >= now) {
    // Case 1: No target date OR future date → last 12 past quarters
    let year = currentYear;
    let quarter = currentQuarter - 1;

    for (let i = 0; i < 12; i++) {
      if (quarter === 0) {
        quarter = 4;
        year--;
      }
      const q = `Q${quarter} ${year}`;
      quarters.push({ name: q, ID: q });
      quarter--;
    }
  } else {
    // Case 2: Target date in past → include current quarter, then go back
    const targetYear = parsedTargetDate.getFullYear();
    const targetQuarter = Math.floor(parsedTargetDate.getMonth() / 3) + 1;

    // Total quarters between target and now (inclusive of current)
    const totalQuarters =
      (currentYear - targetYear) * 4 + (currentQuarter - targetQuarter);
    const numOptions = Math.min(totalQuarters + 1, 12); // +1 for current quarter

    let year = currentYear;
    let quarter = currentQuarter;

    for (let i = 0; i < numOptions; i++) {
      if (quarter === 0) {
        quarter = 4;
        year--;
      }
      const q = `Q${quarter} ${year}`;
      quarters.push({ name: q, ID: q });
      quarter--;
    }
  }

  return quarters;
};

export const getOtherYearOptions = (targetDate) => {
  const years = [];
  const now = new Date();
  const currentYear = now.getFullYear();

  const parsedTargetDate = targetDate ? new Date(targetDate) : null;

  if (!parsedTargetDate || parsedTargetDate >= now) {
    // Case 1: No target date OR future date → last 10 years (exclude current year)
    for (let i = 1; i <= 10; i++) {
      const year = currentYear - i;
      years.push({ name: year.toString(), ID: year.toString() });
    }
  } else {
    // Case 2: Target date in past → include current year, then go back
    const targetYear = parsedTargetDate.getFullYear();

    // Total years between target and now (inclusive of current year)
    const totalYears = currentYear - targetYear;
    const numOptions = Math.min(totalYears + 1, 10);

    for (let i = 0; i < numOptions; i++) {
      const year = currentYear - i;
      years.push({ name: year.toString(), ID: year.toString() });
    }
  }

  return years;
};


export const getFinYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let i = 0; i < 5; i++) {
    const year = currentYear - i;
    options.push(`${year}-${year + 1}`);
  }
  return options;
};

export const isPendingTasks = (threadID, errorMessage = 'Cannot perform this action.', errorDescription = 'Please complete tasks before proceeding.', showToast = true, version = 1) => {
  const thread = getThreadByID(threadID)
  const tasks = JSON.parse(thread?.connectionData?.Tasks) || [];

  const hasPendingTasks = tasks.some(task => {
    const taskThreadIDs = task.TIDs ? task.TIDs.split(',').map(id => id.trim()) : [];
    return taskThreadIDs.includes(String(thread?.ThreadID)) && task.Sts === 1;
  });

  if (hasPendingTasks) {
    if (showToast) {
      toast.error(errorMessage, { description: errorDescription, position: version === 2 ? 'top-center' : undefined });
    }
    return true; // Has pending tasks
  }

  return false; // No pending tasks

};

export const getConnectionByID = (connID) => {
  if (!connID) return null;

  const state = store.getState();
  const connectionData = state.connectionData?.data || [];
  const numericConnID = parseInt(connID);

  return connectionData.find(conn =>
    conn.ConnID === numericConnID ||
    conn.ConnID === connID ||
    conn.NodeID === numericConnID ||
    conn.NodeID === connID
  ) || null;
};

export const getThreadByID = (threadID) => {
  if (!threadID) return null;

  const state = store.getState();
  const threadData = state.threadData?.data || [];
  const numericThreadID = parseInt(threadID);

  return threadData.find(thread =>
    thread.ThreadID === numericThreadID ||
    thread.ThreadID === threadID ||
    thread.ThreadID === String(threadID)
  ) || null;
};

export const getThreadsByConnID = (connID) => {
  if (!connID) return [];

  // First get the connection to access OpenThreadIDs
  const connection = getConnectionByID(connID);

  if (!connection || !connection.OpenThreadIDs) {
    return [];
  }

  const openThreadIDsArr = connection.OpenThreadIDs
    .split(",")
    .map(id => id.trim())
    .filter(id => id);

  if (openThreadIDsArr.length === 0) {
    return [];
  }

  const state = store.getState();
  const allThreads = state.threadData?.data || [];

  // Filter threads that match the OpenThreadIDs
  return allThreads.filter(thread =>
    openThreadIDsArr.includes(String(thread.ThreadID))
  );
};

export const getConnectionsByIDs = (connIDs = []) => {
  if (!Array.isArray(connIDs) || connIDs.length === 0) return [];

  const state = store.getState();
  const connectionData = state.connectionData?.data || [];

  return connIDs.map(id => {
    const numericID = parseInt(id);
    return connectionData.find(conn =>
      conn.ConnID === numericID ||
      conn.ConnID === id ||
      conn.NodeID === numericID ||
      conn.NodeID === id
    );
  }).filter(Boolean); // Remove null/undefined results
};

export const getThreadsByIDs = (threadIDs = []) => {
  if (!Array.isArray(threadIDs) || threadIDs.length === 0) return [];

  const state = store.getState();
  const threadData = state.threadData?.data || [];

  return threadIDs.map(id => {
    const numericID = parseInt(id);
    return threadData.find(thread =>
      thread.ThreadID === numericID ||
      thread.ThreadID === id ||
      thread.ThreadID === String(id)
    );
  }).filter(Boolean); // Remove null/undefined results
};
export const getTransactionTypeById = (typeId) => {
  const state = store.getState();
  const types = getTransactionTypesArr(state);
  return types.find(type => type.XnTID === typeId) || null;
};

export const getCurrencySymbol = (currencyID) => {
  if (!currencyID) return '₹'; // Default to INR

  const currency = currencies.find(c => c.CurrencyID === parseInt(currencyID));
  return currency?.Symbol || '₹';
};

export const FormatAmount = ({
  amount,
  currency = "₹",
  amtClassName = "",
  wholeCss = "",
  showCurr = true,
  showZero = false,
  showDecimalPoints = false, // can be 0,1,2,3...
}) => {
  const hasDecimalConfig = typeof showDecimalPoints === "number";

  const zeroText = hasDecimalConfig
    ? Number(0).toFixed(showDecimalPoints)
    : "0";

  // Handle amount = 0
  if (amount === 0) {
    return (
      <span className={`inline-flex items-center gap-1 ${wholeCss}`}>
        <span className={amtClassName}>{showZero ? zeroText : "-"}</span>
      </span>
    );
  }

  // Handle null / undefined / empty
  if (amount == null || amount === "") {
    return (
      <span className={`inline-flex items-center gap-1 ${wholeCss}`}>
        {showCurr && (
          <span className={amtClassName} style={{ color: "grey" }}>
            {currency}
          </span>
        )}
        <span className={amtClassName}>{zeroText}</span>
      </span>
    );
  }

  // Locale selection
  const state = store.getState();
  const countryID = state.agencyData?.data?.CountryID;
  const locale = countryID === 1 ? "en-IN" : "en-US";

  const numAmount = Number(amount);

  const formatted = numAmount.toLocaleString(
    locale,
    hasDecimalConfig
      ? {
        minimumFractionDigits: showDecimalPoints,
        maximumFractionDigits: showDecimalPoints,
      }
      : {
        maximumFractionDigits: 2,
      }
  );

  return (
    <span className={`inline-flex items-center gap-1 ${wholeCss}`}>
      {showCurr && <span className={amtClassName}>{currency}</span>}
      <span className={amtClassName}>{formatted}</span>
    </span>
  );
};

export const getTransactionIcon = (transactionType) => {
  if (!transactionType) {
    return <SahiIcon iconName="DollarSign" className="h-4 w-4 shrink-0 flex-shrink-0" />;
  }

  // First check if the transaction type has its own icon
  if (transactionType.Icon && transactionType.Icon.trim() !== '') {
    return <SahiIcon iconName={transactionType.Icon} className="h-4 w-4 shrink-0 flex-shrink-0" />;
  }
};

// Get icon color based on transaction type
export const getTransactionIconColorClass = (transactionType) => {
  const type = transactionType?.Typ || '';
  switch (type) {
    case 'S': return 'text-sahi-green'; // Sales
    case 'P': return 'text-sahi-orange'; // Purchase
    default: return 'text-sahi-blue';
  }
};

export const pluralize = (count, singular, plural) =>
  count === 1 ? singular : plural;


export const sortByDate = (data, dateKey, order = 'asc') => {
  return [...data].filter(item => item && item[dateKey]).sort((a, b) => {
    const dateA = new Date(a[dateKey]);
    const dateB = new Date(b[dateKey]);
    return order === 'asc' ? dateA - dateB : dateB - dateA;
  });
}

export const getMobileHeaderTitle = (text, maxLength = 28) => {
  if (!text || text.length <= maxLength) {
    return text || '';
  }
  return `${text.substring(0, maxLength)}...`;
};


export const getCurrentUser = () => {
  try {
    const authData = getAuthData();
    if (!authData?.UserID) {
      console.warn('No authenticated user found');
      return null;
    }

    const usersData = getStorageData('U') || [];
    const currentUser = usersData.find(user =>
      user.UserID === authData.UserID && user.IsRemove === 0
    );

    return currentUser || null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const getCurrentUserProperty = (propertyName, defaultValue = null) => {
  try {
    const currentUser = getCurrentUser();
    if (!currentUser) {
      console.warn('Current user not found');
      return defaultValue;
    }

    // Check if property exists in user object
    if (propertyName in currentUser) {
      return currentUser[propertyName];
    }

    return defaultValue;
  } catch (error) {
    console.error(`Error getting user property '${propertyName}':`, error);
    return defaultValue;
  }
};

export const getAllUser = async () => {
  try {
    const usersData = await getStorageData('U') || [];
    const activeUsers = usersData.filter(user => user.IsRemove === 0);
    return activeUsers;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const currentUserHasPermission = (permission) => {
  const value = getCurrentUserProperty(permission, false);
  return value;
};

export const userHasPermissionForAgency = (permission, agencyID) => {
  try {
    if (!agencyID || String(agencyID) === '0') {
      return currentUserHasPermission(permission);
    }

    const myAgenciesData = getStorageData('MyAgencies') || [];
    const matchingAgency = myAgenciesData.find(agn => String(agn.AgencyID) === String(agencyID));

    if (!matchingAgency) {
      const currentUser = getCurrentUser();
      if (currentUser && String(currentUser.AgencyID) === String(agencyID)) {
        return currentUser[permission] !== undefined ? currentUser[permission] : false;
      }
      return false;
    }

    const targetUserID = matchingAgency.UserID;
    const usersData = getStorageData('U') || [];
    const targetUser = usersData.find(user =>
      user.UserID === targetUserID && user.IsRemove === 0
    );

    if (!targetUser) {
      return false;
    }

    return targetUser[permission] !== undefined ? targetUser[permission] : false;
  } catch (error) {
    console.error(`Error checking permission '${permission}' for agency '${agencyID}':`, error);
    return false;
  }
};

export const getAgencyApiOptions = (agencyID) => {
  if (!agencyID) return {};
  try {
    const myAgenciesData = getStorageData('MyAgencies') || [];
    const matchingAgency = myAgenciesData.find(agn => String(agn.AgencyID) === String(agencyID));
    if (matchingAgency) {
      return {
        forAgencyID: matchingAgency.AgencyID,
        userID: matchingAgency.UserID
      };
    }
  } catch (error) {
    console.error('Error getting agency API options:', error);
  }
  return {};
};

export const getAgencyNameByID = (agencyID) => {
  if (!agencyID) return '';
  try {
    const numericAgencyID = Number(agencyID);

    // 1. Check 'B' table in localStorage (Agencies master table)
    const bRaw = localStorage.getItem('B');
    if (bRaw) {
      const bData = JSON.parse(bRaw);
      const bRows = Array.isArray(bData) ? bData : [bData];
      const bMatch = bRows.find(agn => Number(agn?.AgencyID ?? agn?.NodeID) === numericAgencyID);
      const agnName = bMatch?.AgencyName || bMatch?.CompanyName || bMatch?.BizName || bMatch?.AgnName;
      if (agnName) return agnName;
    }

    // 2. Check 'MyAgencies' in localStorage
    const myAgenciesRaw = localStorage.getItem('MyAgencies');
    if (myAgenciesRaw) {
      const myAgencies = JSON.parse(myAgenciesRaw);
      if (Array.isArray(myAgencies)) {
        const matchingAgency = myAgencies.find(agn => Number(agn?.AgencyID) === numericAgencyID);
        const agnName = matchingAgency?.AgencyName || matchingAgency?.CompanyName || matchingAgency?.BizName || matchingAgency?.AgnName;
        if (agnName) return agnName;
      }
    }

  } catch (error) {
    console.error('Error getting agency name by ID:', error);
  }
  return '';
};

export const checkUserRights = () => {
  let rights = {
    canSeeCstrs: false,
    canSeeSplrs: false,
    addThr: false,
    addConns: false,
    addTxn: false,
    addCtrcs: false,
    agnConfig: false,
  }
  const currentUser = getCurrentUser();
  if (currentUser) {
    if ([1, 2, 4, 5].includes(currentUser?.ConnViewTypeID)) {
      rights.canSeeCstrs = true;
    }
    if ([1, 3, 4, 5].includes(currentUser?.ConnViewTypeID)) {
      rights.canSeeSplrs = true;
    }
    if (currentUser?.ThrActions >= 1 && currentUser?.ThrActions <= 3) {
      rights.addThr = true;
    }
    if (currentUser?.ConnEntry) {
      rights.addConns = true;
    }
    if (currentUser?.XnActions >= 1 && currentUser?.XnActions <= 3) {
      rights.addTxn = true;
    }
    if (currentUser?.CtrcActions >= 1 && currentUser?.CtrcActions <= 3) {
      rights.addCtrcs = true;
    }
    if (currentUser?.AgnConfig) {
      rights.agnConfig = true;
    }
  }
  return rights;
}

export const validatorObj = {
  // Validates email format using regex pattern
  validateEmail: (email) => /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(email),

  // Validates mobile number based on country (India = 10 digits, others = 7-15 digits)
  validateMobile: (mobile, countryID) => {
    if (!mobile) return false;
    return countryID === 1 ? /^\d{10}$/.test(mobile) : /^\d{7,15}$/.test(mobile);
  },

  // Validate mobile number using country code (cc)
  validateMobileByCC: (mobile, cc) => {
    if (!mobile) return false;
    return cc === '91' ? /^[0-9]\d{9}$/.test(mobile) : /^\d{7,15}$/.test(mobile);
  },

  // validateDate function
  validateDate: (dateStr, options = {}) => {
    const { allowFuture = true, allowPast = true, isRequired = false } = options;

    if (!isRequired && !dateStr?.trim()) return null;
    if (isRequired && !dateStr?.trim()) return 'Date is required';

    const trimmed = String(dateStr).trim().replace(/\s+/g, ' '); // Clean up multiple spaces

    // Simplified regex check - focus on the most common formats
    const dateFormats = [
      /^\d{4}-\d{2}-\d{2}$/,                                    // 2024-01-15
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/,                // 2024-01-15T10:30:45
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z?$/,       // 2024-01-15T10:30:45.000Z
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/,                // 2024-01-15 10:30:45
      /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/,                      // 2024-01-15 10:30
      /^\d{1,2}\/\d{1,2}\/\d{4}$/,                            // 1/15/2024 or 01/15/2024
      /^\d{1,2}-\d{1,2}-\d{4}$/                               // 1-15-2024 or 01-15-2024
    ];

    const isValidFormat = dateFormats.some(regex => regex.test(trimmed));

    // Remove the format validation error - let the Date parsing handle it

    if (!isValidFormat) {
      return 'Invalid date format. Use: yyyy-MM-dd (e.g., 2024-01-15)';
    }

    // Simplified date parsing - try direct parsing first
    let dateToValidate;

    try {
      // Try direct parsing first (works for yyyy-MM-dd format)
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        dateToValidate = new Date(trimmed + 'T00:00:00');
      } else if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) {
        dateToValidate = new Date(trimmed);
      } else if (/^\d{4}-\d{2}-\d{2} /.test(trimmed)) {
        dateToValidate = new Date(trimmed.replace(' ', 'T'));
      } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        const parts = trimmed.split('/');
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        dateToValidate = new Date(`${year}-${month}-${day}T00:00:00`);
      } else if (/^\d{1,2}-\d{1,2}-\d{4}$/.test(trimmed)) {
        const parts = trimmed.split('-');
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        dateToValidate = new Date(`${year}-${month}-${day}T00:00:00`);
      } else {
        dateToValidate = new Date(trimmed);
      }

      if (isNaN(dateToValidate.getTime())) {
        return 'Invalid date. Please use format: yyyy-MM-dd (e.g., 2024-01-15)';
      }

      const today = new Date();
      dateToValidate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      if (!allowFuture && dateToValidate > today) return 'Date cannot be in the future';
      if (!allowPast && dateToValidate < today) return 'Date cannot be in the past';

      return null;

    } catch (error) {
      return 'Invalid date. Please use format: yyyy-MM-dd (e.g., 2024-01-15)';
    }
  },

  // Enhanced formatSmallDateTime to handle all formats
  formatSmallDateTime: (dateString) => {
    if (!dateString || dateString === 'null' || dateString === 'undefined') {
      return null;
    }

    try {
      let date;
      const trimmed = String(dateString).trim();

      // Handle different input formats
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        // yyyy-MM-dd format - add time
        date = new Date(trimmed + 'T00:00:00');
      } else if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}(:\d{2})?$/.test(trimmed)) {
        // yyyy-MM-dd HH:mm:ss or yyyy-MM-dd HH:mm format - convert space to T
        date = new Date(trimmed.replace(' ', 'T'));
      } else if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z?$/.test(trimmed)) {
        // Excel ISO format with milliseconds: 2024-04-19T18:29:50.000Z
        date = new Date(trimmed);
      } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(trimmed)) {
        // MM/dd/yyyy format
        const parts = trimmed.split('/');
        const month = parts[0].padStart(2, '0');
        const day = parts[1].padStart(2, '0');
        const year = parts[2];
        date = new Date(`${year}-${month}-${day}T00:00:00`);
      } else {
        // Already in ISO format or other format
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) return null;

      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');

      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    } catch {
      return null;
    }
  }
};

export const isApp = () => {
  try {
    const authData = getAuthData();
    return authData?.IsWeb ? false : true;
  } catch (error) {
    throw error;
  }
}

export const formatLabel = (label) => {
  if (!label) return "";
  return label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
};

export function passesRelativeDateFilter(filterValue, dateValue) {
  if (!filterValue || filterValue === "all") return true;
  const now = new Date();
  const d = dateValue ? new Date(dateValue) : null;

  switch (filterValue) {
    case "None":
    case "Not Set":
      return !d;
    case "Today":
      return d && isSameDay(d, now);
    case "Tomorrow":
      return d && isSameDay(d, addDays(now, 1));
    case "Yesterday":
      return d && isSameDay(d, addDays(now, -1));
    case "Overdue":
      return d && d < now;
    case "Last 7 days":
      return d && d >= addDays(now, -7);
    case "Last 30 days":
      return d && d >= addDays(now, -30);
    case "Last 3 months":
      return d && d >= addMonths(now, -3);
    case "Last 6 months":
      return d && d >= addMonths(now, -6);
    case "More than 6 months":
      return d && d < addMonths(now, -6);
    case "Next 7 days":
      return d && d <= addDays(now, 7) && d >= now;
    case "Next 30 days":
      return d && d <= addDays(now, 30) && d >= now;
    case "This Month":
      return d && d >= startOfMonth(now) && d <= endOfMonth(now);
    case "This Quarter":
      return d && d >= startOfQuarter(now) && d <= endOfQuarter(now);
    case "This Year":
      return d && d >= startOfYear(now) && d <= endOfYear(now);
    default:
      return true;
  }
}

export const truncateText = (text, max = 30) => {
  if (!text) return "";
  return text.length > max ? text.slice(0, max) + "…" : text;
};

export const isRecordInDateRange = (recordDate, dateRange, finYear, otherMonth, otherQuarter, otherYear) => {
  const recordDT = new Date(recordDate);
  const now = new Date();

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfRecord = new Date(
    recordDT.getFullYear(),
    recordDT.getMonth(),
    recordDT.getDate()
  );

  switch (dateRange) {
    case "Today":
      return startOfRecord.getTime() === startOfToday.getTime();

    case "Tomorrow": {
      const tomorrow = new Date(startOfToday);
      tomorrow.setDate(tomorrow.getDate() + 1);
      return startOfRecord.getTime() === tomorrow.getTime();
    }

    case "Week Ahead": {
      const endOfWeekAhead = new Date(startOfToday);
      endOfWeekAhead.setDate(endOfWeekAhead.getDate() + 7);

      return (
        startOfRecord > startOfToday &&
        startOfRecord <= endOfWeekAhead
      );
    }

    case "Overdue":
      return startOfRecord < startOfToday;

    case "This Month":
      return recordDT.getMonth() === now.getMonth() &&
        recordDT.getFullYear() === now.getFullYear();

    case "Last Month":
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
      return recordDT.getMonth() === lastMonth.getMonth() &&
        recordDT.getFullYear() === lastMonth.getFullYear();

    case "This Quarter":
      const currentQuarter = Math.floor(now.getMonth() / 3);
      const recordQuarter = Math.floor(recordDT.getMonth() / 3);
      return recordQuarter === currentQuarter &&
        recordDT.getFullYear() === now.getFullYear();

    case "Prev Quarter":
      const prevQuarterDate = new Date(now.getFullYear(), now.getMonth() - 3);
      const prevQuarter = Math.floor(prevQuarterDate.getMonth() / 3);
      return Math.floor(recordDT.getMonth() / 3) === prevQuarter &&
        recordDT.getFullYear() === prevQuarterDate.getFullYear();

    case "Other Quarter":
      if (otherQuarter === "all") return true;
      const [quarterStr, yearStr] = otherQuarter.split(' ');
      const quarterNum = parseInt(quarterStr.replace('Q', ''));
      const yr = parseInt(yearStr);
      const recordQ = Math.floor(recordDT.getMonth() / 3) + 1;
      return recordQ === quarterNum && recordDT.getFullYear() === yr;

    case "This Year":
      return recordDT.getFullYear() === now.getFullYear();

    case "Other Year":
      if (otherYear === "all") return true;
      return recordDT.getFullYear() === parseInt(otherYear);

    case "Fin Year":
      if (finYear === "all") return true;
      const [startYear, endYear] = finYear.split('-').map(Number);
      const recordYear = recordDT.getFullYear();
      const recordMonth = recordDT.getMonth();

      if (recordMonth >= 3) {
        return recordYear === startYear;
      } else {
        return recordYear === endYear;
      }

    case "Other Month":
      if (otherMonth === "all") return true;
      const [monthName, year] = otherMonth.split(' ');
      const monthIndex = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        .indexOf(monthName);
      return recordDT.getMonth() === monthIndex &&
        recordDT.getFullYear() === parseInt(year);

    default:
      return true;
  }
};

export const getNestedValue = (obj, path) => {
  return path.split(".").reduce((o, k) => (o ? o[k] : ""), obj);
};


// Emoji mapping based on feedback score (1-5)
export const sentimentEmoji = (score, className) => {
  switch (score) {
    case 0:
      return <Smile className={`inline-block h-6 w-6 text-sahi-grey ${className}`} />;
    case 1:
      return <Angry className={`inline-block h-6 w-6 text-sahi-red ${className}`} />;
    case 2:
      return <Frown className={`inline-block h-6 w-6 text-sahi-orange ${className}`} />;
    case 3:
      return <Meh className={`inline-block h-6 w-6 text-sahi-dark-blue ${className}`} />;
    case 4:
      return <Smile className={`inline-block h-6 w-6 text-sahi-blue ${className}`} />;
    case 5:
      return <Laugh className={`inline-block h-6 w-6 text-sahi-green ${className}`} />;
    default:
      return <Meh className={`inline-block h-6 w-6 text-gray-500 ${className}`} />;
  }
};

export const getFdbkScoreText = (score) => {
  switch (score) {
    case 0: return "Unknown"
    case 1: return "Very Unhappy";
    case 2: return "Somewhat Unhappy";
    case 3: return "Neutral";
    case 4: return "Somewhat Happy";
    case 5: return "Very Happy";
    default: return "Unknown";
  }
}

// helper that uses getThreadTypeMeta internally
export const getThreadTypeIcon = (threadTypeID, threadTypes, extraClasses) => {
  const meta = getThreadTypeMeta(threadTypeID, threadTypes);

  // merge colorClass from meta with any extraClasses you want to pass
  const appliedClass = `${meta.colorClass} h-4 w-4 ${extraClasses}`;

  // meta.Icon is a React component
  return <meta.Icon className={appliedClass} />;
};

export const getContractIcon = (ctrcType) => {
  // You'll need to map ctrcType to appropriate icons
  switch (ctrcType) {
    case 1:
      return <FileText className="w-4 h-4 text-sahi-green" />;
    default:
      return <FileText className="w-4 h-4 text-sahi-orange" />;
  }
};



// Dynamic transaction type mapping - builds mapping from Redux store
export const getTransactionTypeMapping = () => {
  const state = store.getState();
  const types = getTransactionTypesArr(state);

  const mapping = {};
  types.forEach(type => {
    if (type.XnTNm && type.XnTID) {
      mapping[type.XnTNm] = type.XnTID;
    }
  });

  return mapping;
};

// Get transaction type ID by name (dynamic version)
export const getTransactionTypeIdByName = (typeName) => {
  const mapping = getTransactionTypeMapping();
  return mapping[typeName] || null;
};

// Alternative: Direct lookup without creating full mapping (more efficient for single lookups)
export const findTransactionTypeByName = (typeName) => {
  const state = store.getState();
  const types = getTransactionTypesArr(state);
  return types.find(type => type.XnTNm === typeName) || null;
};

export const getThreadTypeName = (isSingular, typeID, threadTypes, isCpitalise) => {
  let threadType = threadTypes.filter(thread => Number(thread.TID) === Number(typeID));
  let result = isSingular ? threadType[0]?.TypSng : threadType[0]?.Typ;
  if (threadType.length !== 0 && !isCpitalise) {
    result = result.toLowerCase();
  }
  return result;
}

// Validate TaxID
export const validateTaxID = (taxID, selectedStateGSTPrefix, isGST, minLength, maxLength) => {
  if (isGST) {
    if (!taxID) return { isValid: false, message: 'GSTIN is required' };

    // Remove spaces and convert to uppercase
    const cleanGSTIN = taxID.replace(/\s/g, '').toUpperCase();

    // Check length
    if (cleanGSTIN.length !== maxLength) {
      return {
        isValid: false,
        message: `GSTIN must be exactly ${maxLength} characters long`
      };
    }

    // Check if state is selected
    if (!selectedStateGSTPrefix) {
      return {
        isValid: false,
        message: 'Please select a state first'
      };
    }

    // Check if first two digits match state GST prefix
    const gstinPrefix = cleanGSTIN.substring(0, 2);
    if (gstinPrefix !== selectedStateGSTPrefix) {
      return {
        isValid: false,
        message: `Please enter valid GSTIN number.`
      };
    }

    // Basic GSTIN format validation (2 digits + 10 alphanumeric + 1 digit + 1 alphabet + 1 alphanumeric)
    const gstinPattern = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    if (!gstinPattern.test(cleanGSTIN)) {
      return {
        isValid: false,
        message: 'Invalid GSTIN format'
      };
    }
  }
  else {
    if (!taxID) return { isValid: false, message: 'Tax ID is required' };
    const cleanTaxId = taxID.replace(/\s/g, '');

    if (cleanTaxId.length < minLength || cleanTaxId.length > maxLength) {
      return {
        isValid: false,
        message: `Tax ID must be between ${minLength} and ${maxLength} characters long`
      };
    }
  }

  return { isValid: true, message: '' };
};


export const getConnectionOptionsWithSearch = (connections = []) => {
  return connections
    .map(connection => {
      // Parse contacts if it's a string
      let contacts = connection.Contacts;
      if (typeof contacts === 'string') {
        try {
          contacts = JSON.parse(contacts);
        } catch (e) {
          contacts = [];
        }
      }

      // Create searchable text combining business name, city, and contact persons
      const contactNames = [];
      if (connection.ContactName) {
        contactNames.push(connection.ContactName);
      }
      if (contacts && Array.isArray(contacts)) {
        contacts.forEach(contact => {
          if (contact.CNm && !contactNames.includes(contact.CNm)) {
            contactNames.push(contact.CNm);
          }
        });
      }

      const searchableText = [
        connection.BizName || '',
        connection.City || '',
        ...contactNames
      ].filter(Boolean).join(' ').toLowerCase();

      const label = [connection.BizName, connection.ContactName]
        .filter(val => val && val !== '-') // Removes null, empty strings, and "-"
        .join(' / ');

      return {
        value: connection.ConnID,
        label: label,
        searchableText: searchableText,
        connection
      };
    })
    .sort((a, b) => {
      // Safely handle undefined/null labels
      const labelA = a.label || '';
      const labelB = b.label || '';
      return labelA.localeCompare(labelB);
    });
};

// Get stage color based on stage index and activity type
export const thrStageShades = (stageIndex, activityId) => {
  if (activityId === 1 || activityId === 4) {
    const orangeColors = ['#f49966', '#e26f1f', '#bf5700', '#9a4000', '#7a3400'];
    return orangeColors[stageIndex] || '#f97316';
  } else if (activityId === 2 || activityId === 5) {
    const greenColors = ['#63d68c', '#2ea447', '#0b5d1e', '#073613', '#041f0b'];
    return greenColors[stageIndex] || '#22c55e';
  } else if (activityId === 3 || activityId === 6) {
    const blueColors = ['#4db3d1', '#2fa3c7', '#0c6980', '#094f66', '#06394d'];
    return blueColors[stageIndex] || '#0c6980';
  }
  // Fallback for other activity types
  return '#6b7280';
};

export const freemiumEditionID = 4;

export const getSubscriptionPlans = (CountryID = 1) => {
  const isIndianUser = CountryID === 1;

  if (isIndianUser) {
    return [
      {
        id: 'free',
        editionID: 1,
        name: 'Free',
        sName: 'Free',
        icon: <Zap className="w-6 h-6" />,
        color: 'sahi-dark-blue',
        description: 'Perfect for small teams',
        features: [
          '10 Users',
          isMobileDevice() || isMobileBrowser() ? 'Up to 10 contacts' : 'Up to 10 contacts'
        ],
        monthly: {
          price: '0',
          period: '/ month*',
          link: 'https://example.com/sahicrm-lite-monthly'
        },
        yearly: {
          price: '0',
          period: '/ year*',
          originalPrice: '2000',
          discount: 0,
          link: 'https://example.com/sahicrm-lite-yearly'
        },
        yearlyOnly: true,
        show: false,
        rfdAvl: false,
        cntMaxUsers: 10,
        cntMaxConns: 10
      },
      {
        id: 'pro',
        editionID: 2,
        name: 'Pro',
        sName: 'Pro',
        icon: <Star className="w-6 h-6" />,
        color: 'sahi-green',
        description: 'Perfect for growing businesses',
        features: [
          'Unlimited Users',
          isMobileDevice() || isMobileBrowser() ? 'Up to 1000 contacts' : 'Up to 1000 contacts'
        ],
        monthly: {
          price: '500',
          period: '/ month*',
          originalPrice: '500',
          link: 'https://rzp.io/rzp/SahiCRMProMth01'
        },
        yearly: {
          price: '1800',  //'3000',
          period: '/ year*',
          originalPrice: '1800', //'1800',  //'3000', //'6000'
          discount: 0,  //40,
          link: 'https://rzp.io/rzp/SahiCRMProYr0218',
          originalPriceLink: 'https://rzp.io/rzp/SahiCRMProYr0218'  //'https://rzp.io/rzp/SahiCRMProYr01'
        },
        show: false,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: 1000
      },
      {
        id: 'corporate',
        editionID: 3,
        name: 'Corporate',
        sName: 'Corporate',
        icon: <Crown className="w-6 h-6" />,
        color: 'sahi-red',
        description: 'Built for enterprise teams',
        features: [
          'Unlimited Users',
          isMobileDevice() || isMobileBrowser() ? 'Up to 100,000 contacts' : 'Up to 100,000 contacts'
        ],
        monthly: {
          price: '1500',
          period: '/ month*',
          originalPrice: '1500',
          link: 'https://rzp.io/rzp/SahiCRMCorpMth02'
        },
        yearly: {
          price: '9000',
          period: '/ year*',
          originalPrice: '18000',
          discount: 50,
          link: 'https://rzp.io/rzp/SahiCRMCorpYr02',
          originalPriceLink: 'https://rzp.io/rzp/SahiCRMCorpYr0218'
        },
        show: false,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: 100000
      },
      {
        id: 'smartSeller',
        editionID: 4,
        name: 'Smart Seller',
        sName: 'Smart Seller',
        icon: <Star className="w-6 h-6" />,
        color: 'sahi-dark-blue',
        description: 'Perfect for smart sales executives',
        features: [
          '5 Users',
          isMobileDevice() || isMobileBrowser() ? 'Up to 1000 contacts' : 'Up to 1000 contacts'
        ],
        monthly: {
          price: '99',
          period: '/ month*',
          originalPrice: '99',
          link: ''
        },
        yearly: {
          price: '499',  //'600',  
          period: '/ year*',
          originalPrice: '499',  //'600', 
          discount: 0,
          link: '',
          originalPriceLink: ''  //'https://rzp.io/rzp/SahiCRMProYr01'
        },
        show: false,
        rfdAvl: false,
        cntMaxUsers: 1,
        cntMaxConns: 1000
      },
      // {
      //   id: 'smartTeam',
      //   editionID: 5,
      //   name: 'Smart Team',
      //   sName: 'Smart Team',
      //   icon: <Star className="w-6 h-6" />,
      //   color: 'sahi-green',
      //   description: 'Perfect for ambitious sales teams',
      //   features: [
      //     '5 Users',
      //     isMobileDevice() || isMobileBrowser() ? 'Up to 10,000 contacts' : 'Up to 10,000 contacts'
      //   ],
      //   monthly: {
      //     price: '199', //499
      //     period: '/ month*',
      //     originalPrice: '199', //499
      //     link: ''
      //   },
      //   yearly: {
      //     price: '999',  //'3000',
      //     period: '/ year*',
      //     originalPrice: '999', //'3000'
      //     discount: 0,
      //     link: '',
      //     originalPriceLink: ''  //'https://rzp.io/rzp/SahiCRMProYr01'
      //   },
      //   show: false,
      //   rfdAvl: false,
      //   cntMaxUsers: 5,
      //   cntMaxConns: 10000
      // },
      // {
      //   id: 'smartBusiness',
      //   editionID: 5,
      //   name: 'Smart Business',
      //   sName: 'Smart Business',
      //   icon: <Star className="w-6 h-6" />,
      //   color: 'sahi-red',
      //   description: 'Perfect for pro sales management',
      //   features: [
      //     'Unlimited Users',
      //     isMobileDevice() || isMobileBrowser() ? 'Up to 1,00,000 contacts' : 'Up to 1,00,000 contacts'
      //   ],
      //   monthly: {
      //     price: '499',  //'1499',
      //     period: '/ month*',
      //     originalPrice: '499',  //'1499',
      //     link: ''
      //   },
      //   yearly: {
      //     price: '2999',  //'9000',  
      //     period: '/ year*',
      //     originalPrice: '2999',  //'9000',  
      //     discount: 0,  
      //     link: '',
      //     originalPriceLink: ''  //'https://rzp.io/rzp/SahiCRMProYr01'
      //   },
      //   show: false,
      //   rfdAvl: false,
      //   cntMaxUsers: -1,
      //   cntMaxConns: 100000
      // },
      {
        id: 'smartTeam',
        editionID: 5,
        name: 'Smart Team',
        sName: 'Smart Team',
        icon: <Star className="w-6 h-6" />,
        color: 'sahi-red',
        description: 'Perfect for ambitious sales teams',
        features: [
          '10 Users',
          isMobileDevice() || isMobileBrowser() ? 'Up to 10,000 contacts' : 'Up to 10,000 contacts'
        ],
        monthly: {
          price: '499',
          period: '/ month*',
          originalPrice: '499',
          link: ''
        },
        yearly: {
          price: '1200',
          period: '/ year*',
          originalPrice: '1200',
          discount: 0,
          link: '',
          originalPriceLink: ''
        },
        show: true,
        rfdAvl: false,
        cntMaxUsers: 10,
        cntMaxConns: 10000
      },
      {
        id: 'smartBusiness',
        editionID: 6,
        name: 'Smart Business',
        sName: 'Smart Business',
        icon: <Flame className="w-6 h-6" />,
        color: 'sahi-red',
        description: 'Perfect for pro sales management',
        features: [
          'Unlimited Users',
          isMobileDevice() || isMobileBrowser() ? 'Up to 1,00,000 contacts' : 'Up to 1,00,000 contacts'
        ],
        monthly: {
          price: '499',
          period: '/ month*',
          originalPrice: '499',
          link: ''
        },
        yearly: {
          price: '6000',
          period: '/ year*',
          originalPrice: '6000',
          discount: 0,
          link: '',
          originalPriceLink: ''
        },
        show: true,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: 100000
      },
      {
        id: 'smartManagement',
        editionID: 7,
        name: 'Smart Management',
        sName: 'Smart Management',
        icon: <Crown className="w-6 h-6" />,
        color: 'sahi-green',
        description: 'Complete solution for management & teams',
        features: [
          'Unlimited Users',
          'Unlimited Contacts',
          'Unlimited Inquiries'
        ],
        monthly: {
          price: '99',
          period: '/ month*',
          originalPrice: '99',
          link: ''
        },
        yearly: null,
        monthlyOnly: true,
        show: true,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: -1,
        cntMaxThreads: -1
      }
    ];
  } else {
    // International subscription plans
    return [
      {
        id: 'free',
        editionID: 1,
        name: 'Free',
        sName: 'Free',
        icon: <Zap className="w-6 h-6" />,
        color: 'sahi-dark-blue',
        description: 'Perfect for small teams',
        features: [
          '10 Users',
          '10 contacts'
        ],
        yearly: {
          price: '0',
          period: '/ year*',
          originalPrice: '',
          discount: 0,
          link: 'mailto:sales@sahicrm.com'
        },
        yearlyOnly: true,
        show: false,
        rfdAvl: false,
        cntMaxUsers: 10,
        cntMaxConns: 10
      },
      {
        id: 'smartManagement',
        editionID: 7,
        name: 'Smart Management',
        sName: 'Smart Management',
        icon: <Crown className="w-6 h-6" />,
        color: 'sahi-green',
        description: 'Complete solution for management & teams',
        features: [
          'Unlimited Users',
          'Unlimited Contacts',
          'Unlimited Inquiries'
        ],
        monthly: {
          price: '2',
          period: '/ month*',
          originalPrice: '2',
          link: ''
        },
        yearly: null,
        monthlyOnly: true,
        show: true,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: -1,
        cntMaxThreads: -1
      },
      {
        id: 'pro',
        editionID: 2,
        name: 'Pro',
        sName: 'Pro',
        icon: <Star className="w-6 h-6" />,
        color: 'sahi-green',
        description: 'Perfect for growing businesses',
        features: [
          'Unlimited Users',
          '10,000 contacts'
        ],
        yearly: {
          price: '60',
          period: '/ year*',
          originalPrice: '60',
          discount: 0,
          link: 'https://rzp.io/rzp/SahiCRMProYr02Int'
        },
        yearlyOnly: true,
        show: true,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: 10
      },
      {
        id: 'corporate',
        editionID: 3,
        name: 'Corporate',
        sName: 'Corporate',
        icon: <Crown className="w-6 h-6" />,
        color: 'sahi-red',
        description: 'Built for enterprise teams',
        features: [
          'Unlimited Users',
          '100,000 contacts'
        ],
        yearly: {
          price: '300',
          period: '/ year*',
          originalPrice: '600',
          discount: 50,
          link: 'https://rzp.io/rzp/SahiCRMCorpYr02Int'
        },
        yearlyOnly: true,
        show: true,
        rfdAvl: false,
        cntMaxUsers: -1,
        cntMaxConns: 100000
      }
    ];
  }
};

export const getThreadModeTitle = (accessThrs) => {
  if (accessThrs.length == 1) {
    return {
      typSng: accessThrs[0].TypSng,
      typPlr: accessThrs[0].Typ,
      typSngLower: accessThrs[0].TypSng.toLowerCase(),
      typPlrLower: accessThrs[0].Typ.toLowerCase()
    };
  }
  return {
    typSng: "Thread",
    typPlr: "Threads",
    typSngLower: "thread",
    typPlrLower: "threads"
  };
}

// Function which is used for account subscription type
export const isPremium = () => {
  const state = store.getState();
  const agencyData = state.agencyData.data;
  return agencyData?.SubTypeID > 1;
}

// Function to check if the current user is a premium user across their agencies
export const isUserPremium = () => {
  try {
    const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);

    const bCookieRaw = localStorage.getItem('B');
    if (!bCookieRaw) return false;

    const data = JSON.parse(bCookieRaw);
    const rows = Array.isArray(data) ? data : [data];

    let isPremium = false;
    for (const agency of rows) {
      const isAgencyPremium = Number(agency?.SubTypeID) > 1;
      const isAdminOfAgency = Number(agency?.AdminUserID) > 0 && myUserIDs.includes(Number(agency?.AdminUserID));
      if (isAgencyPremium && isAdminOfAgency) {
        isPremium = true;
        break;
      }
    }
    return isPremium;
  } catch (error) {
    console.error("Error in isUserPremium:", error);
    return false;
  }
};

export const isTrial = () => {
  const state = store.getState();
  const agencyData = state.agencyData.data;
  return (agencyData?.SubType?.includes("Free") || agencyData?.SubType === "Freemium") && agencyData?.LastUpgdDT == null;
}

export const isMobileBrowser = () => {
  if (!isMobileDevice()) return false;

  const userAgent = navigator.userAgent.toLowerCase();

  // Check for WebView indicators (WebViewGold and Native WebView)
  // Added "applewebkit" + absence of "safari" check which is common for iOS WebViews
  const isWebView = userAgent.includes('wv') ||
    userAgent.includes('webview') ||
    userAgent.includes('webviewgold') ||
    userAgent.includes('wvg') ||
    userAgent.includes('; wv)') ||
    (userAgent.includes('iphone') && userAgent.includes('applewebkit') && !userAgent.includes('safari'));

  // Check for PWA standalone mode (installed as app)
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;

  // If WebView or Standalone detected, it's NOT a mobile browser
  return !isWebView && !isStandalone;
};

// Detect if running in mobile app (WebViewGold or Native WebView)
export const isMobileApp = () => {
  if (!isMobileDevice()) return false;

  // If it's a mobile device but NOT a mobile browser, it's an app
  return !isMobileBrowser();
};

export const createRazorpayOptions = (
  agencyData,
  userData,
  handlePaymentSuccess,
  handlePaymentDismiss,
  orderDetails
) => {
  const description = 'SahiClub Premium Subscription';
  const isMobileWebView = isMobileApp();

  const isIndianUser = Number(agencyData?.CountryID || 1) === 1;
  const currencyCode = orderDetails?.currency || orderDetails?.CurrencyCode || (isIndianUser ? 'INR' : 'USD');

  // Format mobile number with country calling code if available
  let formattedMobile = agencyData?.Mobile ? String(agencyData.Mobile).trim() : '';
  if (formattedMobile) {
    if (!formattedMobile.startsWith('+')) {
      const countryObj = countries.find(c => c.CountryID === Number(agencyData?.CountryID || 1));
      const cc = agencyData?.CC || agencyData?.CountryCodeVal || countryObj?.CC || (isIndianUser ? '91' : '');
      if (cc && !formattedMobile.startsWith(cc)) {
        formattedMobile = `+${cc}${formattedMobile.replace(/^0+/, '')}`;
      } else if (cc && formattedMobile.startsWith(cc)) {
        formattedMobile = `+${formattedMobile}`;
      }
    }
  }

  const amountInSmallestUnit = orderDetails?.amtInPaise || orderDetails?.amount; // Fallback for safety

  const options = {
    key: orderDetails?.rzrpKey,
    amount: amountInSmallestUnit,
    currency: currencyCode,
    name: 'Biziverse Pvt. Ltd.',
    description: description,
    image: `https://Biziverse.com/images/BiziverseSquare.jpg`,
    order_id: orderDetails?.rzrpOrderID,
    handler: (response) => {
      handlePaymentSuccess(response);
    },
    prefill: {
      name: agencyData?.AgencyName,
      contact: formattedMobile,
      email: agencyData?.Email
    },
    notes: {
      "address": "SahiClub Premium Subscription - " + orderDetails?.rzrpOrderID.toString()
    },
    theme: {
      color: '#0b5d1e',
    },
    modal: {
      ondismiss: () => {
        handlePaymentDismiss();
      },
    },
    ...(isMobileWebView && {
      webview_intent: true,
    }),
  };

  return options;
};

export const getSalesMode = () => {
  return 1; // 0 - Freemium, 1 - Trial, 2 - Paid
};

export const getImageRoot = (folder, imageName) => {
  let imagePath = '';
  if (folder === 'Landing') {
    imagePath = isLive() ? "/Web/assets/Images/Landing/" : "/assets/Images/Landing/";
  }
  else if (folder === 'Images') {
    imagePath = isLive() ? "/Web/assets/Images/" : "/assets/Images/"
  }
  else if (folder === 'Training') {
    imagePath = isLive() ? "/Web/assets/Images/Training/" : "/assets/Images/Training/";
  }
  return imagePath + imageName;
}

export const isThrStgExpird = (lastStageChgDT, threadTypeID, stageID) => {
  if (!lastStageChgDT) return false;
  const state = store.getState();
  const threadStages = state.masterData?.threadStages || []
  const nowDT = new Date();
  const stageClosingDay = threadStages?.find((thrStage) => thrStage.TID === threadTypeID)?.Stages?.find((stage) => stage.SID === stageID)?.D;
  const stageDT = new Date(lastStageChgDT);
  const expitrdStageDT = addDays(stageDT, stageClosingDay);
  return isAfter(nowDT, expitrdStageDT);
}

export const formatRecordText = (text, fulltext = false) => {
  if (!text) return "No notes available";
  const state = store.getState();
  const countryID = state.agencyData?.data?.CountryID;
  const match = text.match(/^Order of (.+?)(\d+(?:\.\d+)?) booked by (.*)$/);

  if (isMobileDevice()) {
    if (match) {
      return `Order of ${match[1]}${formatAmount(match[2], countryID)} booked by ${match[3]}`;
    }
  }

  if (match) {
    return (
      <>
        Order of {match[1]}<FormatAmount amount={match[2]} showCurr={false} countryID={countryID} wholeCss="inline-flex" /> booked by {match[3]}
      </>
    );
  }

  if (text.length > 150 && !fulltext) {
    return text.substring(0, 150) + "...";
  }
  return text;
};

export const calculateCouponExpiry = (cpnType, days) => {
  if (cpnType != 'signUp' || !days) {
    return
  }
  const state = store.getState();
  const agencyData = state.agencyData.data;
  if (!agencyData?.CreatedDT) return null;

  const createdDate = new Date(agencyData.CreatedDT);
  const expiryDate = new Date(createdDate);
  expiryDate.setDate(expiryDate.getDate() + days);
  expiryDate.setHours(23, 59, 0, 0);

  return expiryDate;
};

export const isCouponExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date() > expiryDate;
};

export const allCoupons = [
  //   {
  //   id: 1,
  //   couponCode: 'SAVE500',
  //   description: 'Get flat Rs. 500 off on your first subscription',
  //   discount: 500,
  //   discountType: 'amount', // 'amount' or 'percentage'
  //   period: 'yearly',
  //   cpnType: 'signUp',
  //   showTimer: true,
  //   validTillDT: null,
  //   subType: 'SUB',
  //   editionID: [2,3],
  //   proAmt: 1300,
  //   corpAmt: 8500
  // },
  {
    id: 2,
    couponCode: 'SAVE100',
    description: 'Get flat Rs. 100 off on your subscription',
    discount: 100,
    discountType: 'amount', // 'amount' or 'percentage'
    period: 'monthly',
    cpnType: '',
    showTimer: false,
    validTillDT: null,
    subType: 'SUB',
    editionID: [2, 3],
    proAmt: 400,
    corpAmt: 1400
  }, {
    id: 3,
    couponCode: 'SAVE200',
    description: 'Get flat Rs. 200 off on your subscription',
    discount: 200,
    discountType: 'amount', // 'amount' or 'percentage'
    period: 'yearly',
    cpnType: '',
    showTimer: false,
    validTillDT: null,
    subType: 'SUB',
    editionID: [2, 3],
    proAmt: 1600,
    corpAmt: 8800
  }, {
    id: 4,
    couponCode: 'SPLOFFER',
    description: 'Get flat Rs. 180 off on your first subscription',
    discount: 180,
    discountType: 'amount', // 'amount' or 'percentage'
    period: 'monthly',
    cpnType: '',
    showTimer: true,
    validTillDT: null,
    subType: 'SUB',
    editionID: [2],
    proAmt: 320,
    corpAmt: null
  },
  // {
  //   id: 6,
  //   couponCode: 'FLASHOFFER',
  //   description: 'Get your first subscription at just Rs. 99 (First Year)',
  //   discount: 1716.1,
  //   discountType: 'amount', // 'amount' or 'percentage'
  //   period: 'yearly',
  //   cpnType: '',
  //   showTimer: false,
  //   validTillDT: null,
  //   subType: 'SUB',
  //   editionID: [2],
  //   proAmt: 99,
  //   corpAmt: null
  // }
  {
    id: 5,
    couponCode: 'FLAT30',
    description: 'Get flat 30% off on your first subscription',
    discount: 30,
    discountType: 'percentage', // 'amount' or 'percentage'
    period: 'yearly',
    cpnType: 'SUB',
    showTimer: true,
    validTillDT: null,
    subType: 'SUB',
    editionID: [2, 3],
    proAmt: 1260, // 1800 - 1800 * 30%
    corpAmt: 6300 // 9000 - 9000 * 30%
  }
  // ,{
  //   id: 6,
  //   couponCode: 'NEWCRM',
  //   description: 'Get your first subscription at just Rs. 500 (First Year)',
  //   discount: 1300,
  //   discountType: 'amount', // 'amount' or 'percentage'
  //   period: 'yearly',
  //   cpnType: '',
  //   showTimer: true,
  //   validTillDT: null,
  //   subType: 'SUB',
  //   editionID: [2],
  //   proAmt: 500,
  //   corpAmt: null 
  // },{
  //   id: 7,
  //   couponCode: 'NEWCRM', // coupon for blackout period
  //   description: 'Get your first subscription at just Rs. 500 (First Year)',
  //   discount: 1300,
  //   discountType: 'amount', // 'amount' or 'percentage'
  //   period: 'yearly',
  //   cpnType: '',
  //   showTimer: false,
  //   validTillDT: null,
  //   subType: 'SUB',
  //   editionID: [2],
  //   proAmt: 500,
  //   corpAmt: null
  // }
  , {
    id: 8,
    couponCode: 'SPLOFR',
    description: 'Start First Year @ just 99',
    discount: 1101,
    discountType: 'amount', // 'amount' or 'percentage'
    period: 'yearly',
    cpnType: '',
    showTimer: false,
    validTillDT: null,
    subType: 'SUB',
    editionID: [5],
    proAmt: 0,
    corpAmt: 0
  }
  , {
    id: 9,
    couponCode: 'FLAT500',
    description: 'Get flat Rs. 500 off on your first yearly subscription',
    discount: 500,
    discountType: 'amount', // 'amount' or 'percentage'
    period: 'yearly',
    cpnType: '',
    showTimer: false,
    validTillDT: null,
    subType: 'SUB',
    editionID: [6],
    proAmt: 0,
    corpAmt: 0
  }
]

export const isInBlackoutPeriod = () => {
  const now = new Date();
  const hour = now.getHours();
  return hour < 20 || hour >= 24; // 20:00 to 23:59
};

export const getCoupons = (editionID, selectedBillingPeriod, subType) => {
  const state = store.getState();
  const agencyData = state.agencyData.data;

  return allCoupons
    .map((coupon) => {
      // NEWCRM (id: 6) - Valid for 15 minutes after createdDT
      if (coupon.id === 6 && agencyData?.CreatedDT) {
        return {
          ...coupon,
          validTillDT: addMinutes(new Date(agencyData.CreatedDT), 15)
        };
      }

      // FLAT30 (id: 5) - Valid till next day at 23:59:00 based on SubTillDT
      if (coupon.id === 5 && agencyData?.SubTillDT) {
        const subTillDate = new Date(agencyData.SubTillDT);
        const nextDay = addDays(subTillDate, 1);
        nextDay.setHours(23, 59, 59, 999);
        return {
          ...coupon,
          validTillDT: nextDay
        };
      }

      return coupon;
    })
    .filter(coupon => {
      // Exclude coupon ID 7 during blackout period (8 PM - 12 AM)
      if (coupon.id === 7 && isInBlackoutPeriod()) {
        return false;
      }

      const editionIDArray = Array.isArray(coupon.editionID) ? coupon.editionID : [coupon.editionID];
      const matchesEdition = editionIDArray.length === 0 || editionIDArray.includes(editionID);

      // Standard filtering logic
      return coupon.period === selectedBillingPeriod
        && matchesEdition
        && (coupon.validTillDT === null || !isCouponExpired(coupon.validTillDT))
        && (coupon.subType === '' || coupon.subType === subType);
    })
    .sort((a, b) => a.id - b.id);
};

export const getProductTypeByName = (pName) => {
  const state = store.getState();
  const products = state.masterData.products;
  if (!Array.isArray(products) || !pName) return null;

  const foundedProduct = products.find(
    (p) => p.PNm.toLowerCase() === pName.toLowerCase().trim()
  );

  if (!foundedProduct) return null;

  return {
    PTID: foundedProduct.PTID,
    type: foundedProduct.PTID === 1 ? "Product" : foundedProduct.PTID === 2 ? "Service" : "Unknown"
  };
};


// Helper to get current TimeZone
export const getTimeZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch (e) {
    return ''; // Backend handles empty strings by defaulting to IST
  }
};

// export const canEditTask = (taskObj) => {
//     const user = getCurrentUser();
//     if((taskObj?.createdUserID && taskObj?.pocUserID) || (taskObj?.CreatedUserID && taskObj?.POCUserID))
//     {
//        if(taskObj?.createdUserID == user.UserID || taskObj?.pocUserID == user.UserID || 
//          taskObj?.CreatedUserID == user.UserID || taskObj?.POCUserID  == user.UserID
//        )
//        {
//          return true;
//        }
//     }
//     return false
// } 

export const getMyAgenciesMappedIDs = (key = 'UserID') => {
  try {
    const raw = localStorage.getItem('MyAgencies');
    if (!raw) return [];
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.map(item => Number(item[key])).filter(id => !isNaN(id) && id > 0);
  } catch (e) {
    console.error("Error in getMyAgenciesMappedIDs:", e);
    return [];
  }
};

export const getMyAgencyIDForUserID = (userID) => {
  try {
    if (!userID) return 0;
    const raw = localStorage.getItem('MyAgencies');
    if (!raw) return 0;
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return 0;
    const match = list.find(item => String(item?.UserID) === String(userID));
    return Number(match?.AgencyID) || 0;
  } catch (e) {
    console.error("Error in getMyAgencyIDForUserID:", e);
    return 0;
  }
};

export const findUserByIDOrMobile = (users, userID, mobileStr = null) => {
  if (!users || !Array.isArray(users)) return null;

  // 1. Try finding by UserID first
  if (userID && userID !== '0' && userID !== 0) {
    const foundByID = users.find(u => Number(u.userID || u.UserID) === Number(userID));
    if (foundByID) return foundByID;
  }

  // 2. Fallback: Try finding by Mobile (+ CC)
  if (mobileStr) {
    const cleanTaskMobile = String(mobileStr).replace(/[^0-9]/g, '');
    if (cleanTaskMobile) {
      const foundByMobile = users.find(u => {
        const uMob = String(u.mobile || u.Mobile || '').replace(/[^0-9]/g, '');
        if (!uMob) return false;
        const uCC = String(u.cc || u.CC || '').replace(/[^0-9]/g, '');
        const uFull = uCC + uMob;

        return (
          cleanTaskMobile === uMob ||
          cleanTaskMobile === uFull ||
          cleanTaskMobile.endsWith(uMob) ||
          (uMob.length >= 7 && cleanTaskMobile.endsWith(uMob))
        );
      });

      if (foundByMobile) return foundByMobile;
    }
  }

  return null;
};

export const extractObserverUserIDs = (taskObj) => {
  if (!taskObj) return [];

  const rawObs = taskObj?.observerUserIDs ?? taskObj?.ObserverUserIDs ?? taskObj?.ObserverUIDs ?? taskObj?.observerUIDs ?? taskObj?.ObserverUids ?? taskObj?.observerUids ?? taskObj?.obsUIDs ?? taskObj?.ObsUIDs;

  let uids = [];

  if (typeof rawObs === 'string' && rawObs.trim()) {
    uids = rawObs.split(',').map(id => Number(id.trim())).filter(id => !isNaN(id) && id > 0);
  } else if (Array.isArray(rawObs) && rawObs.length > 0) {
    uids = rawObs.map(Number).filter(id => !isNaN(id) && id > 0);
  }

  if (uids.length > 0) {
    return [...new Set(uids)];
  }

  const uActions = taskObj?.observerUActions ?? taskObj?.ObserverUActions ?? taskObj?.obsUActions ?? taskObj?.ObsUActions;
  let actionsArr = [];
  if (typeof uActions === 'string' && uActions.trim()) {
    try {
      actionsArr = JSON.parse(uActions);
    } catch (e) { }
  } else if (Array.isArray(uActions)) {
    actionsArr = uActions;
  }

  if (Array.isArray(actionsArr) && actionsArr.length > 0) {
    const fromActions = actionsArr
      .map(item => Number(item?.OUID ?? item?.ouid ?? item?.UserID ?? item?.userID ?? 0))
      .filter(id => !isNaN(id) && id > 0);
    if (fromActions.length > 0) {
      return [...new Set(fromActions)];
    }
  }

  return [];
};

export const resolveTaskAgencyID = (taskObj, fallbackAgencyID = 0) => {
  if (!taskObj) return Number(fallbackAgencyID || 0);

  const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);

  // 1. Check if POC (Assignee) is in myUserIDs - Assignee takes highest priority
  const pocUserID = Number(taskObj?.pocUserID || taskObj?.POCUserID || taskObj?.uID || taskObj?.UserID || 0);
  if (pocUserID && myUserIDs.includes(pocUserID)) {
    const pocAgencyID = getMyAgencyIDForUserID(pocUserID);
    if (pocAgencyID) return pocAgencyID;
  }

  // 2. Check if an explicit taskAgencyID or AgencyID was specified (e.g. from TaskEntry or DB task record)
  const explicitAgencyID = Number(taskObj?.taskAgencyID || taskObj?.agencyID || taskObj?.AgencyID || 0);
  if (explicitAgencyID && explicitAgencyID > 0) {
    return explicitAgencyID;
  }

  // 3. Check if Creator is in myUserIDs
  const createdUserID = Number(taskObj?.createdUserID || taskObj?.CreatedUserID || taskObj?.uIDCreated || 0);
  if (createdUserID && myUserIDs.includes(createdUserID)) {
    const creatorAgencyID = getMyAgencyIDForUserID(createdUserID);
    if (creatorAgencyID) return creatorAgencyID;
  }

  // 4. Check if any Observer is in myUserIDs
  const observerUids = taskObj?.observerUids || taskObj?.ObserverUids || taskObj?.ObserverUIDs || taskObj?.observerUserIDs || taskObj?.ObserverUserIDs || taskObj?.obsUIDs || taskObj?.ObsUIDs || '';
  const observerUidsArr = observerUids
    ? String(observerUids).split(',').map(id => Number(id.trim())).filter(id => !isNaN(id) && id > 0)
    : [];
  const matchedObserverUID = observerUidsArr.find(id => myUserIDs.includes(id));
  if (matchedObserverUID) {
    const observerAgencyID = getMyAgencyIDForUserID(matchedObserverUID);
    if (observerAgencyID) return observerAgencyID;
  }

  // 5. Fallback to fallbackAgencyID
  return Number(fallbackAgencyID || 0);
};

export const canEditTask = (taskObj) => {
  const user = getCurrentUser();

  if (!user || !taskObj) return false;

  const taskClass = taskObj?.Class ?? taskObj?.class ?? taskObj?.taskClass ?? taskObj?.TaskClass;
  if (taskClass !== undefined && taskClass !== null && (Number(taskClass) === 3 || Number(taskClass) === 4)) {
    return false;
  }

  const taskStatus = taskObj?.status ?? taskObj?.Status ?? taskObj?.sts ?? taskObj?.Sts;
  if (taskStatus !== undefined && taskStatus !== null && Number(taskStatus) !== 1) {
    return false;
  }

  const taskCreatorID = Number(taskObj?.createdUserID || taskObj?.CreatedUserID || taskObj?.CreatedUID || taskObj?.UID || taskObj?.uid || 0);
  if (!taskCreatorID) return false;

  if (taskCreatorID === Number(user.UserID)) {
    return true;
  }

  const myUserIDs = getMyAgenciesMappedIDs('UserID');
  return myUserIDs.includes(taskCreatorID);
};

export const getTaskCategoryIDForUser = (taskObj, userID) => {
  if (!taskObj || !userID) return 0;

  const assigneeID = Number(taskObj?.pocUserID ?? taskObj?.POCUserID ?? taskObj?.uID ?? taskObj?.UID ?? 0);
  const creatorID = Number(taskObj?.createdUserID ?? taskObj?.CreatedUserID ?? taskObj?.CreatedUID ?? 0);

  const myUserIDs = getMyAgenciesMappedIDs('UserID').map(Number);
  const isAssignee = assigneeID === Number(userID) || myUserIDs.includes(assigneeID);
  const isCreator = creatorID === Number(userID) || myUserIDs.includes(creatorID);

  const pocCtgID = Number(taskObj?.pocCtgID ?? taskObj?.POCCtgID ?? 0);
  const createdCtgID = Number(taskObj?.createdCtgID ?? taskObj?.CreatedCtgID ?? 0);

  if (isAssignee && isCreator) {
    return pocCtgID || createdCtgID;
  }
  if (isAssignee) {
    return pocCtgID;
  }
  if (isCreator) {
    return createdCtgID;
  }
  return 0;
};

export const getTaskPriorityForUser = (taskObj, userID) => {
  if (!taskObj) return '';

  const assigneeID = Number(taskObj?.pocUserID ?? taskObj?.POCUserID ?? taskObj?.uID ?? taskObj?.UID ?? 0);
  const creatorID = Number(taskObj?.createdUserID ?? taskObj?.CreatedUserID ?? taskObj?.CreatedUID ?? 0);

  const myUserIDs = getMyAgenciesMappedIDs('UserID');
  const isCreatorMe = creatorID === Number(userID) || myUserIDs.includes(creatorID);
  const isAssigneeMe = assigneeID === Number(userID) || myUserIDs.includes(assigneeID);

  const pri = taskObj?.pri ?? taskObj?.priority ?? taskObj?.Priority ?? '';
  const pocPri = taskObj?.pocPriority ?? taskObj?.POCPriority ?? taskObj?.ppri ?? taskObj?.PPri ?? '';

  if (isCreatorMe && isAssigneeMe) {
    return (pri == 'H' || pocPri == 'H') ? 'H' : pri;
  }

  if (isAssigneeMe) {
    return pocPri || 'M';
  }
  if (isCreatorMe) {
    return pri;
  }
  return 'M';
};

export const getTaskTitleForUser = (taskObj, userID) => {
  const creatorTitle = taskObj?.ttl ?? taskObj?.title ?? taskObj?.Ttl ?? taskObj?.Task ?? '';
  if (!taskObj || !userID) return creatorTitle;

  const assigneeID = Number(taskObj?.pocUserID ?? taskObj?.POCUserID ?? taskObj?.uID ?? taskObj?.UID ?? 0);
  const creatorID = Number(taskObj?.createdUserID ?? taskObj?.CreatedUserID ?? taskObj?.CreatedUID ?? 0);

  const myUserIDs = getMyAgenciesMappedIDs('UserID');
  const isAssignee = assigneeID === Number(userID) || myUserIDs.includes(assigneeID);

  if (isAssignee) {
    const pocTitle = taskObj?.pocTask ?? taskObj?.POCTask ?? '';
    return pocTitle || creatorTitle;
  }
  return creatorTitle;
};

export const getUploadUrl = (relativePath) => {
  if (!relativePath) return '';
  const baseUrl = API_CONFIG.BASE_URL || '';
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
  const cleanRelativePath = relativePath.startsWith('/') ? relativePath.substring(1) : relativePath;
  return `${cleanBaseUrl}${cleanRelativePath}`;
};

export const getCommunityRights = (currentUser, agencyData) => {
  const isAdmin = currentUser?.UserID === agencyData?.AdminUserID || !!currentUser?.AgnConfig;
  return {
    canViewFeed: !!currentUser,
    canPostFeed: !!currentUser,
    canEditCompanyProfile: isAdmin,
  };
};

export const isAgencyProfileComplete = (agencyData) => {
  if (!agencyData) return false;
  const hasName = !!(agencyData.AgencyName?.trim() || agencyData.CompanyName?.trim() || agencyData.BizName?.trim());
  const hasCity = !!agencyData.City?.trim();
  const hasState = !!(agencyData.StateID || agencyData.StateName?.trim() || agencyData.State?.trim());
  return !!(hasName && hasCity && hasState);
};

export const isWebstoreComplete = (masterData) => {
  return true;
  if (!masterData) return false;
  const hasBizTypes = !!(masterData.bizTypes && String(masterData.bizTypes).trim());
  const hasSellToDesc = !!(masterData.sellToDesc && String(masterData.sellToDesc).trim());
  const hasTagline = !!(masterData.tagline && String(masterData.tagline).trim());
  const hasDescription = !!(masterData.description && String(masterData.description).trim());
  const hasSegment = !!(masterData.segmentID && Number(masterData.segmentID) > 0);
  return !!(hasBizTypes && hasSellToDesc && hasTagline && hasDescription && hasSegment);
};

export const urlToFile = async (url, filename = "share-image.png", isCard = false) => {
  if (!url) return null;
  try {
    const response = await fetch(url, { mode: 'cors' });
    if (!response.ok) return null;
    const blob = await response.blob();
    const mimeType = blob.type || (filename.endsWith('.png') ? 'image/png' : 'image/jpeg');

    if (!isCard || typeof document === 'undefined') {
      return new File([blob], filename, { type: mimeType });
    }

    return new Promise((resolve) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(blob);
      img.onload = () => {
        try {
          const targetSize = 1000;
          const canvas = document.createElement("canvas");
          canvas.width = targetSize;
          canvas.height = targetSize;
          const ctx = canvas.getContext("2d");

          if (!ctx) {
            URL.revokeObjectURL(objectUrl);
            return resolve(new File([blob], filename, { type: mimeType }));
          }

          ctx.fillStyle = "#FFFFFF";
          ctx.fillRect(0, 0, targetSize, targetSize);

          const maxDimension = targetSize * 0.8;
          const imgWidth = img.naturalWidth || img.width || 1;
          const imgHeight = img.naturalHeight || img.height || 1;
          const scale = Math.min(maxDimension / imgWidth, maxDimension / imgHeight, 1.5);

          const drawWidth = Math.round(imgWidth * scale);
          const drawHeight = Math.round(imgHeight * scale);
          const dx = Math.round((targetSize - drawWidth) / 2);
          const dy = Math.round((targetSize - drawHeight) / 2);

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(img, dx, dy, drawWidth, drawHeight);

          canvas.toBlob((canvasBlob) => {
            URL.revokeObjectURL(objectUrl);
            if (canvasBlob) {
              resolve(new File([canvasBlob], filename.replace(/\.[^/.]+$/, "") + ".png", { type: "image/png" }));
            } else {
              resolve(new File([blob], filename, { type: mimeType }));
            }
          }, "image/png", 1.0);
        } catch (e) {
          URL.revokeObjectURL(objectUrl);
          resolve(new File([blob], filename, { type: mimeType }));
        }
      };

      img.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(new File([blob], filename, { type: mimeType }));
      };

      img.src = objectUrl;
    });
  } catch (err) {
    console.warn("Could not convert url to File:", err);
    return null;
  }
};

export const shareWithImage = async ({
  title = "",
  text = "",
  url = "",
  imageUrl = "",
  imageFileName = "share.png",
  successCopyMessage = "Details copied to clipboard!"
}) => {
  const isAndroidWebView = /Android/i.test(navigator.userAgent) && /wv/.test(navigator.userAgent);
  const isNativeBrowser = typeof navigator !== 'undefined' && navigator.share && !isAndroidWebView;

  let shareFile = null;

  if (imageUrl && isNativeBrowser) {
    try {
      const resolvedImgUrl = imageUrl.startsWith("http") || imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")
        ? imageUrl
        : `${window.location.origin}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;
      shareFile = await urlToFile(resolvedImgUrl, imageFileName, false);
    } catch (err) {
      console.warn("Failed to convert image to file:", err);
      shareFile = null;
    }
  }

  if (typeof navigator !== 'undefined' && navigator.share) {
    try {
      const isUrlInText = url && text && text.includes(url);
      const shareData = {
        title: title || undefined,
        text: text || undefined,
        url: (!isUrlInText && url) ? url : ""
      };

      if (shareFile) {
        shareData.files = [shareFile];
      }
      else if (imageUrl && isAndroidWebView) {
        shareData.imageUrl = imageUrl;
      }

      if (navigator.canShare && !navigator.canShare(shareData)) {
        if (shareData.files) {
          delete shareData.files;
          delete shareData.imageUrl;
        }
      }

      await navigator.share(shareData);
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return; // User cancelled share
      console.warn("navigator.share failed:", err);
    }
  }

  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.share) {
    try {
      window.webkit.messageHandlers.share.postMessage({
        title,
        text,
        url: url || "",
        imageUrl: imageUrl || ""
      });
      return;
    } catch (iosErr) {
      console.warn("iOS WebKit share failed:", iosErr);
    }
  }

  if (window.flutter_inappwebview) {
    try {
      window.flutter_inappwebview.callHandler('share', {
        title,
        text,
        url: url || "",
        imageUrl: imageUrl || ""
      });
      return;
    } catch (flutterErr) {
      console.warn("Flutter share handler failed:", flutterErr);
    }
  }

  if (isAndroidWebView) {
    try {
      let intentUrl = "";

      if (imageUrl) {
        try {
          const resolvedImgUrl = imageUrl.startsWith("http") || imageUrl.startsWith("data:") || imageUrl.startsWith("blob:")
            ? imageUrl
            : `${window.location.origin}${imageUrl.startsWith("/") ? "" : "/"}${imageUrl}`;

          let intentExtras = "";
          if (text) {
            intentExtras += `S.android.intent.extra.TEXT=${encodeURIComponent(text)};`;
          }
          if (title) {
            intentExtras += `S.android.intent.extra.SUBJECT=${encodeURIComponent(title)};`;
          }
          if (resolvedImgUrl) {
            intentExtras += `S.android.intent.extra.STREAM=${encodeURIComponent(resolvedImgUrl)};`;
          }

          intentUrl = `intent:#Intent;action=android.intent.action.SEND;type=image/*;${intentExtras}end`;
        } catch (imgErr) {
          console.warn("Android image intent build failed, falling back to text intent:", imgErr);
          intentUrl = "";
        }
      }

      if (!intentUrl) {
        const shareText = text || url || "Check this out!";
        intentUrl = `intent:#Intent;action=android.intent.action.SEND;type=text/plain;S.android.intent.extra.TEXT=${encodeURIComponent(shareText)};end`;
      }

      window.location.href = intentUrl;
      return;
    } catch (e) {
      console.error('Android intent share error:', e);
    }
  }

  if (isMobileDevice()) {
    try {
      const shareText = text || url || "Check this out!";
      const waUrl = `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`;
      openInExternalBrowser(waUrl);
      return;
    } catch (e) {
      console.warn("WhatsApp fallback failed:", e);
    }
  }

  try {
    const copyText = text || url || "Copied to clipboard";
    await navigator.clipboard.writeText(copyText);
    toast.success(successCopyMessage);
  } catch (err) {
    console.error("Clipboard copy failed:", err);
    toast.error("Failed to copy details.");
  }
};

/**
 * Calculates candidate reminder time (in 15-min intervals) before a given due date/time.
 * Tries 60m, 45m, 30m, 15m before dueDT in chronological order.
 */
export const getBeforeDueReminder = (dueDT, nowTime = new Date()) => {
  if (!dueDT || isNaN(dueDT.getTime())) return null;

  for (const offsetMin of [60, 45, 30, 15]) {
    const candidate = new Date(dueDT.getTime() - offsetMin * 60 * 1000);
    const cMin = candidate.getMinutes();
    candidate.setMinutes(cMin - (cMin % 15), 0, 0);
    if (candidate < dueDT && candidate > nowTime) {
      return candidate;
    }
  }

  return null;
};

/**
 * Common utility to calculate the suggested reminder date and time for a task.
 * 
 * Logic:
 * 1. If due is in the future (> today), check daily plan time on that future due date first.
 * 2. If due is today:
 *    - Step 1: Check today's daily plan time if available (> now and < dueDT).
 *    - Step 2: Check nearest 15-minute intervals before due time (60m, 45m, 30m, 15m before dueDT).
 *    - Step 3: If all before due time have passed, fallback to the due time itself on today.
 */
export const calculateSuggestedReminder = (dueDateTime, userPlanTime = '10:00', now = new Date()) => {
  const [planH, planM] = userPlanTime.split(':').map(Number);

  // 0. If task is overdue (dueDateTime is already in the past):
  // Check if today's plan time is still in the future (> now).
  // If yes, suggest today's plan time; if not available (> now), suggest tomorrow's plan time.
  if (dueDateTime && !isNaN(dueDateTime.getTime()) && dueDateTime < now) {
    const todayPlanDT = new Date(now);
    todayPlanDT.setHours(planH, planM, 0, 0);
    if (todayPlanDT > now) {
      return { date: todayPlanDT, time: userPlanTime };
    }
    const tmrwPlanDT = new Date(now);
    tmrwPlanDT.setDate(tmrwPlanDT.getDate() + 1);
    tmrwPlanDT.setHours(planH, planM, 0, 0);
    return { date: tmrwPlanDT, time: userPlanTime };
  }

  const isDueToday = dueDateTime && !isNaN(dueDateTime.getTime()) &&
    format(dueDateTime, 'yyyy-MM-dd') === format(now, 'yyyy-MM-dd');

  // 1. If due is on a future date: check plan time on that due date first
  if (dueDateTime && !isNaN(dueDateTime.getTime()) && !isDueToday && dueDateTime > now) {
    const planTimeOnDueDate = new Date(dueDateTime);
    planTimeOnDueDate.setHours(planH, planM, 0, 0);
    if (planTimeOnDueDate > now && planTimeOnDueDate < dueDateTime) {
      return { date: planTimeOnDueDate, time: userPlanTime };
    }
    const reminderTime = getBeforeDueReminder(dueDateTime, now);
    if (reminderTime && reminderTime > now && reminderTime < dueDateTime) {
      return { date: reminderTime, time: format(reminderTime, 'HH:mm') };
    }
  }

  // 2. For Today or when dueDateTime is today:
  // Step 1: Check today's plan time if available (> now and < dueDateTime)
  const todayPlanDT = new Date(now);
  todayPlanDT.setHours(planH, planM, 0, 0);
  if (todayPlanDT > now && (!dueDateTime || todayPlanDT < dueDateTime)) {
    return { date: todayPlanDT, time: userPlanTime };
  }

  // Step 2: Check nearest 15-min intervals before due time
  if (dueDateTime && !isNaN(dueDateTime.getTime()) && dueDateTime > now) {
    const reminderTime = getBeforeDueReminder(dueDateTime, now);
    if (reminderTime && reminderTime > now && reminderTime < dueDateTime) {
      return { date: reminderTime, time: format(reminderTime, 'HH:mm') };
    }
    const dueTime15 = new Date(dueDateTime);
    const dMin = dueTime15.getMinutes();
    dueTime15.setMinutes(dMin - (dMin % 15), 0, 0);
    return { date: dueDateTime, time: format(dueTime15, 'HH:mm') };
  }

  // Fallback for today if no due time specified
  const nowRounded15 = new Date(now);
  const curMin = nowRounded15.getMinutes();
  nowRounded15.setMinutes(curMin + (15 - (curMin % 15)), 0, 0);
  return { date: nowRounded15, time: format(nowRounded15, 'HH:mm') };
};


export const getTaskCompletionWhatsAppMessage = (task, statusNote = "") => {
  const taskClass = Number(task?.class ?? task?.Class ?? task?.taskClass ?? task?.TaskClass ?? 1);
  const title = task?.ttl || task?.title || task?.taskName || task?.Ttl || task?.Task || '';
  const trimmedNote = (statusNote || '').trim();

  if (taskClass === 3) {
    // Decision task
    if (trimmedNote) {
      return `I have taken a decision on: ${title}\nDecision: ${trimmedNote}`;
    }
    return `I have taken a decision on: ${title}`;
  }

  if (taskClass === 2) {
    // Meeting task
    if (trimmedNote) {
      return `I have completed the meeting: ${title}\nMeeting Notes: ${trimmedNote}`;
    }
    return `I have completed the meeting: ${title}`;
  }

  if (taskClass === 4) {
    // Approval task
    const noteLower = trimmedNote.toLowerCase();
    const isDisapproved = noteLower.startsWith('disapprov') || noteLower.startsWith('reject');
    const actionWord = isDisapproved ? 'disapproved' : 'approved';

    // Check if the note is just "Approved" or "Disapproved"
    const isDefaultKeywordOnly = noteLower === 'approved' || noteLower === 'disapproved' || noteLower === 'approve' || noteLower === 'disapprove';

    if (trimmedNote && !isDefaultKeywordOnly) {
      return `I have ${actionWord}: ${title}\nNotes: ${trimmedNote}`;
    }
    return `I have ${actionWord}: ${title}`;
  }

  // Normal task
  if (trimmedNote) {
    return `I have completed the task: ${title}\nNotes: ${trimmedNote}`;
  }
  return `I have completed the task: ${title}`;
};

export const getTaskActionWhatsAppMessage = ({
  action, // 'DONE' | 'CANCEL' | 'REJECT' | 'REOPEN'
  task,
  statusNote = '',
  isAssigneeMe = false,
}) => {
  const taskClass = Number(task?.class ?? task?.Class ?? task?.taskClass ?? task?.TaskClass ?? 1);
  const title = task?.ttl || task?.title || task?.taskName || task?.Ttl || task?.Task || '';
  const trimmedNote = (statusNote || '').trim();

  if (action === 'DONE') {
    return getTaskCompletionWhatsAppMessage(task, statusNote);
  }

  if (action === 'CANCEL') {
    if (taskClass === 2) {
      // Meeting
      if (isAssigneeMe) {
        return trimmedNote
          ? `I am unable to attend the meeting: ${title}\nReason: ${trimmedNote}`
          : `I am unable to attend the meeting: ${title}`;
      }
      return trimmedNote
        ? `I have cancelled the meeting: ${title}\nReason: ${trimmedNote}`
        : `I have cancelled the meeting: ${title}`;
    }

    if (taskClass === 3) {
      // Decision
      if (isAssigneeMe) {
        return trimmedNote
          ? `I am not taking up the decision: ${title}\nReason: ${trimmedNote}`
          : `I am not taking up the decision: ${title}`;
      }
      return trimmedNote
        ? `I have cancelled the decision: ${title}\nReason: ${trimmedNote}`
        : `I have cancelled the decision: ${title}`;
    }

    if (taskClass === 4) {
      // Approval
      if (isAssigneeMe) {
        return trimmedNote
          ? `I have rejected the approval request: ${title}\nNotes: ${trimmedNote}`
          : `I have rejected the approval request: ${title}`;
      }
      return trimmedNote
        ? `I have cancelled the approval request: ${title}\nReason: ${trimmedNote}`
        : `I have cancelled the approval request: ${title}`;
    }

    // Normal Task
    if (isAssigneeMe) {
      return trimmedNote
        ? `I am not taking up the task: ${title}\nReason: ${trimmedNote}`
        : `I am not taking up the task: ${title}`;
    }
    return trimmedNote
      ? `I have cancelled the task: ${title}\nReason: ${trimmedNote}`
      : `I have cancelled the task: ${title}`;
  }

  if (action === 'REJECT') {
    if (taskClass === 2) {
      return trimmedNote
        ? `I have declined the meeting: ${title}\nReason: ${trimmedNote}`
        : `I have declined the meeting: ${title}`;
    }
    if (taskClass === 3) {
      return trimmedNote
        ? `I am unable to decide on: ${title}\nReason: ${trimmedNote}`
        : `I am unable to decide on: ${title}`;
    }
    if (taskClass === 4) {
      const noteLower = trimmedNote.toLowerCase();
      const isDefaultKeywordOnly = noteLower === 'disapproved' || noteLower === 'rejected' || noteLower === 'disapprove' || noteLower === 'reject';
      if (trimmedNote && !isDefaultKeywordOnly) {
        return `I have rejected: ${title}\nNotes: ${trimmedNote}`;
      }
      return `I have rejected: ${title}`;
    }
    // Normal Task
    return trimmedNote
      ? `I have rejected the task: ${title}\nReason: ${trimmedNote}`
      : `I have rejected the task: ${title}`;
  }

  if (action === 'REOPEN') {
    if (taskClass === 2) {
      return `I have rescheduled/reopened the meeting: ${title}\nPlease check schedule.`;
    }
    if (taskClass === 3) {
      return `I have reopened the decision: ${title}\nPlease review and decide.`;
    }
    if (taskClass === 4) {
      return `I have reopened the approval request: ${title}\nPlease review and approve.`;
    }
    // Normal Task
    return `I have reopened the task: ${title}\nPlease complete it.`;
  }

  const classLabel = taskClass === 2 ? 'meeting' : taskClass === 3 ? 'decision' : taskClass === 4 ? 'approval' : 'task';
  return `I have updated the ${classLabel}: ${title}`;
};

export const playTaskSuccessSound = () => {
  try {
    const authData = getAuthData();
    const agencyID = Number(authData?.AgencyID ?? authData?.agencyID ?? 0);
    if (!TESTING_AGENCIES.includes(agencyID)) {
      return;
    }

    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/task-complete.mpeg`);
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.debug('Failed to play task-complete audio file, using fallback sound:', err);
        // Fallback using Web Audio API
        try {
          const AudioContextClass = window.AudioContext || window.webkitAudioContext;
          if (!AudioContextClass) return;
          const ctx = new AudioContextClass();
          if (ctx.state === 'suspended') ctx.resume().catch(() => { });
          const now = ctx.currentTime;
          const notes = [
            { freq: 1046.50, start: 0.00, duration: 0.22, gain: 0.22, type: 'triangle' },
            { freq: 1318.51, start: 0.06, duration: 0.25, gain: 0.25, type: 'sine' },
            { freq: 1567.98, start: 0.12, duration: 0.32, gain: 0.28, type: 'sine' },
            { freq: 2093.00, start: 0.18, duration: 0.65, gain: 0.35, type: 'triangle' },
            { freq: 4186.01, start: 0.18, duration: 0.40, gain: 0.08, type: 'sine' }
          ];
          notes.forEach(({ freq, start, duration, gain, type = 'sine' }) => {
            const osc = ctx.createOscillator();
            const gainNode = ctx.createGain();
            osc.type = type;
            osc.frequency.setValueAtTime(freq, now + start);
            gainNode.gain.setValueAtTime(0.0001, now + start);
            gainNode.gain.exponentialRampToValueAtTime(gain, now + start + 0.012);
            gainNode.gain.exponentialRampToValueAtTime(0.0001, now + start + duration);
            osc.connect(gainNode);
            gainNode.connect(ctx.destination);
            osc.start(now + start);
            osc.stop(now + start + duration);
          });
          setTimeout(() => { if (ctx.state !== 'closed') ctx.close().catch(() => { }); }, 1000);
        } catch (e) { }
      });
    }
  } catch (err) {
    console.debug('Failed to play task completion sound:', err);
  }
};

export const playOrderSound = () => {
  try {
    const authData = getAuthData();
    const agencyID = Number(authData?.AgencyID ?? authData?.agencyID ?? 0);
    if (!TESTING_AGENCIES.includes(agencyID)) {
      return;
    }

    const audio = new Audio(`${process.env.PUBLIC_URL || ''}/order.mp3`);
    const playPromise = audio.play();
    if (playPromise !== undefined) {
      playPromise.catch((err) => {
        console.debug('Failed to play order.mp3:', err);
      });
    }
  } catch (err) {
    console.debug('Failed to play order sound:', err);
  }
};

